/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable max-len */

// ===== Referenced Resources =====
// https://codepen.io/dremar_design/pen/vYmBzRw
// https://codepen.io/rachsmith/pen/ONVQWv

// ===== Packages =====

import React, {
    useMemo,
    useState,
    useEffect,
    useRef,
    useCallback,
}                                               from 'react';
import {
    Routes,
    Route,
    useMatch,
    useLocation,
}                                               from 'react-router-dom';
import { hot }                                  from 'react-hot-loader';
import { ErrorBoundary }                        from 'react-error-boundary';
import {
    signInAnonymously,
    signOut,
    onAuthStateChanged,
    onIdTokenChanged,
    User,
}                                               from 'firebase/auth';
import {
    doc,
    getFirestore,
    onSnapshot,
    FirestoreError,
    Unsubscribe,
}                                               from 'firebase/firestore';
import ShortUniqueId                            from 'short-unique-id';
import { Buffer }                               from 'buffer';
import process                                  from 'process';

// ===== Firebase =====

import fire                                     from './fire';

// ===== Components =====

import LandingView                              from './components/LandingView';
import AboutView                                from './components/AboutView';
import ReaderView                               from './components/ReaderView';
import PhysicalBookView                         from './components/PhysicalBookView';
import CharactersView                           from './components/CharactersView';
import TreasureChestView                        from './components/TreasureChestView';
import SocialEmergenceView                      from './components/SocialEmergenceView';
import CartView                                 from './components/CartView';
import CheckoutView                             from './components/CheckoutView';
import ErrorFallback                            from './components/ErrorFallback';
import UserProfileDialog                        from './components/UserProfileDialog';
import Snackbar                                 from './components/Snackbar';
import CartButton                               from './components/CartButton';
import NotificationDialog                       from './components/NotificationDialog';
import PrivacyView                              from './components/PrivacyView';
import TermsView                                from './components/TermsView';
import AuthActionView                           from './components/AuthActionView';
import { Transition }                           from './components/Transition';

// ===== Hooks =====

import {
    useInterval,
    useTimeout,
    useEventListener,
    useLatest,
    useHeavyComputeGate,
    useFailureSnackbarLogger,
}                                               from './hooks';

// ===== Services =====

import {
    createAnonymousUser,
    detectBrowser,
    detectTouchDevice,
    getUserIPInfo,
    findParentNodeWithClass,
    getUser,
    getUserPostAccess,
    getUserCartItems,
    isMacOS,
    isMobile,
    recordUserAction,
    updateUser,
    getUserAdminStatus,
    migrateOldUserDataToNewItem,
    startUserSession,
    getUserEmailSessionItemsAndUpdateTimestamps,
    getUserEmail,
    deepEqual,
    restoreStateAfterRedirect,
    saveStateForRedirect,
    extractCredential,
    normalizeTrafficSource,
    inferTrafficSourceFromReferrer,
    shouldBlockIncomingCartSnapshot,
    getLocalCartSyncGuardState,
    getPressEventHandlers,
    getTouchMouseEventHandlers,
    getAuthDebugLog,
    clearAuthDebugLog,
    isAuthDebugEnabled,
    logAuthDebug,
    logAuthUser,
    isTestMode,
    resolveCustomCursorActive,
} from './services';
import shouldUseDarkCursor from './services/shouldUseDarkCursor';
import {
    didUserHydrationSubjectChange,
    shouldIgnoreTransientUserPingPermissionDenied,
    shouldProcessUserPingVersion,
    shouldRefreshUserDerivedCachesAfterAuthHandoff,
} from './services/userHydrationGuards';

// ===== Constants =====

import {
    DEFAULT_CSS_TRANSITION_DURATION,
    HOVER_TARGET_CLASSNAME,
    HEADER_BUTTON_TRANSITION_DURATION,
    FADE_IN_DEFAULT_STYLE,
    FADE_IN_TRANSITION_STYLES,
    DEFAULT_USER_FIRST_NAME,
    DEFAULT_SNACKBAR_VISIBLE_DURATION,
    BODY_FONT_SIZE,
    SUPPORT_EMAIL_ADDRESS,
    DEFAULT_API_PING_VERSION_VALUE,
    NEW_SESSION_TIMEOUT_DURATION,
    MILLISECONDS_IN_A_SECOND,
    EXTENDED_SNACKBAR_VISIBLE_DURATION,
}                                               from './constants/generalConstants';
import CURSOR_SIGN                              from './constants/cursorSigns';

// ===== Enums =====

import {
    PAGE_ROUTE,
    CURSOR_TYPE,
    CURSOR_TARGET,
    INTERACTABLE_OBJECT,
    FIRESTORE_COLLECTION,
    AUTHENTICATION_TYPE,
    FIREBASE_AUTH_SIGN_IN_PROVIDER_ID_TYPE,
    USER_ACTION_TYPE,
    TRAFFIC_SOURCE,
}                                               from './enums';

// ===== Interfaces =====

import {
    ICursorActionCornerPosition,
    ICursorLockedTargetProps,
    ICoord,
    IDimension,
    IPartialUserItem,
    ISnackbarItem,
    IMailingListSubscriptionValue,
    IPostAccessItem,
    ICartItem,
}                                               from './interfaces';

// ===== Images =====

import ChatSign                                 from './images/chat.svg';
import CautionIcon                              from './images/caution.svg';

// ===== Styles =====

import { theme }                                from './themes/theme-context';
import {
    Container,
    CustomCursor,
    CustomCursorSign,
    SoundButtonOuterContainer,
    SoundButtonInnerContainer,
    SoundButton,
    Path,
    SmallCursorActionContainer,
    CursorSight,
    AuthDebugPanelContainer,
    AuthDebugPanelHeader,
    AuthDebugPanelSummary,
    AuthDebugPanelActions,
    AuthDebugPanelButton,
    AuthDebugPanelCopyStatus,
    AuthDebugPanelTextarea,
}                                               from './styles';

// ----- Initialize Buffer and process (for Shippo)
window.Buffer = window.Buffer || Buffer;
window.process = window.process || process;

// ===== Initialize Firebase =====

const { auth, redirectReady, hadPendingRedirect } = fire();

function App(): JSX.Element {
    // ===== General Constants =====

    const SOUND_BUTTON_HEIGHT = 1.375 * BODY_FONT_SIZE;
    const SNACKBAR_MESSAGE_ANONYMOUS_SIGN_IN_ERROR = 'There was a problem signing in as a guest.';
    const SNACKBAR_MESSAGE_REDIRECT_SIGN_IN_ERROR = 'There was a problem signing in. Ensure your browser allows pop-ups and try again.';
    const DEFAULT_SMALL_CURSOR_CORNER: ICursorActionCornerPosition = {
        top: true,
        left: false,
    };
    const INITIAL_CURSOR_SIGN_INDEX = 0;
    const AUTH_REDIRECT_TIMEOUT_DURATION = 1.5 * MILLISECONDS_IN_A_SECOND;
    const ANONYMOUS_SIGN_IN_TIMEOUT_DURATION = 0;
    const ANONYMOUS_SIGN_IN_RETRY_DELAYS_MS = [100, 250, 500];
    const ANONYMOUS_USER_DOC_CONFIRM_RETRY_DELAYS_MS = [0, 75, 150, 300];
    const USER_SESSION_ATTACH_RETRY_DELAYS_MS = [0, 100, 250, 500];
    const ANONYMOUS_CREATE_TIMEOUT_DURATION = 0;
    const LOCAL_READER_PROGRESS_STORAGE_KEY = 'verascope-reader-progress-v1';
    const AUTH_DEBUG_PANEL_POLL_INTERVAL = 1000;
    const AUTH_DEBUG_PANEL_MAX_ENTRIES = 500;

    // ----- General

    const customCursorRef = useRef<HTMLDivElement>(null);
    const smallCursorSightRef = useRef<HTMLDivElement>(null);
    const cursorMousePositionRef = useRef<ICoord | null>(null);
    const cursorPositionAnimationFrameRef = useRef<number | null>(null);
    const anonymousSignInRetryAttemptRef = useRef<number>(0);
    const anonymousSignInRetryTimeoutRef = useRef<number | null>(null);
    const creatingAnonymousUserDocForUidRef = useRef<string | null>(null);
    const signOutToAnonymousStartedAtRef = useRef<number | null>(null);
    const signOutToAnonymousAuthReadyLoggedRef = useRef<boolean>(false);
    const signOutToAnonymousUserReadyLoggedRef = useRef<boolean>(false);
    const initialTrafficSourcePayloadRef = useRef<{
        source: TRAFFIC_SOURCE,
        utmSource: string | null,
        referrer: string | null,
        inferred: boolean,
    } | null>(null);
    // Focus timing diagnostics for dev-only logging.
    const focusStartTimestampRef = useRef<number | null>(Date.now());
    const totalFocusedDurationRef = useRef<number>(0);
    // Guards against briefly applying stale server cart snapshots over fresh local edits.
    // When cart edits are rapid, server reads can lag behind local optimistic state.
    const userCartLocalFreshnessRef = useRef<number>(0);
    const userCartLocalGuardUntilRef = useRef<number>(0);
    const previousSigningInOutRef = useRef<boolean>(false);
    const previousSignedInRef = useRef<AUTHENTICATION_TYPE | null>(null);

    // ===== React Router =====

    const routeIsAuthActionPage = useMatch(PAGE_ROUTE.authAction);
    const routeIsReadPage = useMatch(PAGE_ROUTE.book);
    const routeIsReadPageAnnotationFocused = useMatch(`${PAGE_ROUTE.book}/:annotationId`);
    const routeIsCartPage = useMatch(PAGE_ROUTE.cart);
    const routeIsCheckoutPage = useMatch(`${PAGE_ROUTE.checkout}/:checkpoint`);
    const routeIsPrivacyPage = useMatch(PAGE_ROUTE.privacyPolicy);
    const routeIsTermsPage = useMatch(PAGE_ROUTE.termsOfUse);
    const location = useLocation();

    // ===== State =====
    /**
     * !!!IMPORTANT!!!
     * When you add a new state variable, make sure to add it to the
     * dependencies of the RouterRoutes memo
     */

    const [authInitialized, setAuthInitialized] = React.useState(false);
    // Indicates whether we still have to wait for redirect state to be restored
    const [redirectStateChecked, setRedirectStateChecked] = useState<boolean>(false);
    // Indicates whether browser tab is focused or not (change browser tabs, view browser console, change windows)
    const [browserTabIsFocused, setBrowserTabIsFocused] = useState<number | null>(Date.now());
    // Stores user item data
    const [user, setUser] = useState<IPartialUserItem | null>(null);
    // Indicates the value of the last user update version we've received to compare against new updates
    const [lastUserPingVersion, setLastUserPingVersion] = useState<number>(DEFAULT_API_PING_VERSION_VALUE);
    // Indicates whether user session is being starting avoid additional session initiations
    const [startingUserSession, setStartingUserSession] = useState<boolean>(false);
    // Stores user email
    const [userEmail, setUserEmail] = useState<string | null>(null);
    // Indicates whether we're fetching user email
    const [fetchingUserEmail, setFetchingUserEmail] = useState<boolean>(false);
    // Stores whether user is an admin
    const [userIsAdmin, setUserIsAdmin] = useState<boolean>(false);
    // Stores whether current Firebase auth user email is verified
    const [userEmailVerified, setUserEmailVerified] = useState<boolean>(false);
    // Indicates whether we're fetching user admin status
    const [fetchingUserAdminStatus, setFetchingUserAdminStatus] = useState<boolean>(false);
    // Stores whether user admin status fetch has occurred
    const [fetchedUserAdminStatus, setFetchedUserAdminStatus] = useState<boolean>(false);
    // Stores user post access data
    const [userPostAccess, setUserPostAccess] = useState<Map<string, IPostAccessItem> | undefined>(undefined);
    // Indicates whether we're fetching user post access
    const [fetchingUserPostAccess, setFetchingUserPostAccess] = useState<boolean>(false);
    // Stores user cart items
    const [userCartItems, setUserCartItems] = useState<ICartItem[] | undefined>(undefined);
    // Indicates whether we're fetching user cart items
    const [fetchingUserCartItems, setFetchingUserCartItems] = useState<boolean>(false);
    // Stores the current dimensions of the viewport
    const [viewportDimensions, setViewportDimensions] = useState<IDimension>({
        width: Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0),
        height: Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0),
    });
    // Sound is enabled/disabled in the web app
    const [hasSound, setHasSound] = useState<boolean>(true);
    // Some input in the web app is focused
    const [inputFocused, setInputFocused] = useState<boolean>(false);
    // Indicates whether the custom cursor is active
    const [customCursorActive, setCustomCursorActive] = useState<boolean>(!isMobile());
    // Indicates what type of cursor is currently visible
    const [cursorType, setCursorType] = useState<CURSOR_TYPE>(CURSOR_TYPE.resting);
    // The type of a element being hovered over
    const [cursorTargetType, setCursorTargetType] = useState<CURSOR_TARGET | null>(null);
    // The set of cursor actions to present to the user
    const [cursorSigns, setCursorSigns] = useState<string[]>([]);
    // Cached cursor target
    const [cursorTarget, setCursorTarget] = useState<HTMLElement | undefined>(undefined);
    // Stores the corner at which action icon should be placed on small cursor
    const [smallCursorCorner, setSmallCursorCorner] = useState<ICursorActionCornerPosition>(DEFAULT_SMALL_CURSOR_CORNER);
    // Used to prevent cursor background from forming during transition to small cursor
    const [transitioningToSmallCursor, setTransitioningToSmallCursor] = useState<boolean>(false);
    // Stores properties of target element when cursor locked
    const [cursorLockedTargetProperties, setCursorLockedTargetProperties] = useState<ICursorLockedTargetProps | null>(null);
    // Indicates whether we should check whether to temporarily unlock cursor
    const [checkUnlockCursor, setCheckUnlockCursor] = useState<boolean>(false);
    // Indicates whether cursor can temporarily move, typicall when element grabbed
    const [temporarilyUnlockCursor, setTemporarilyUnlockCursor] = useState<boolean>(false);
    // The index of the current cursor action if there is more than one
    const [cursorSignIndex, setCursorSignIndex] = useState<number>(INITIAL_CURSOR_SIGN_INDEX);
    // indicates whether cursor is hidden
    const [cursorIsHidden, setCursorIsHidden] = useState<boolean>(false);
    // indicates whether logomark has expanded to show menu
    const [landingPageContentRevealed, setLandingPageContentRevealed] = useState<boolean>(false);
    // indicates whether cuneiform book tablet is visible
    const [cuneiformTabletRevealed, setCuneiformTabletRevealed] = useState<boolean>(false);
    // indicates whether user profile dialog is expanded
    const [userProfileDialogExpanded, setUserProfileDialogExpanded] = useState<boolean>(false);
    // indicates whether notification dialog is expanded
    const [notificationDialogExpanded, setNotificationDialogExpanded] = useState<boolean>(false);
    // indicates whether we have initialized user item listener in firestore
    const [subscribedToUserChanges, setSubscribedToUserChanges] = useState<boolean>(false);
    // indicates whether we're signing in or out
    const [signingInOut, setSigningInOut] = useState<boolean>(false);
    // stores id of current session
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
    // stores when current session's last activity started
    const [currentSessionLastActivityStartTimestamp, setCurrentSessionLastActivityStartTimestamp] = useState<number | null>(null);
    // indicates whether to present sign up notify message in user profile dialog
    // occurs when user tries to create an annotation while not signed in
    const [notifyUserToSignUpAfterAnnotationCreationAttempt, setNotifyUserToSignUpAfterAnnotationCreationAttempt] = useState<boolean>(false);
    // indicates whether snackbar is visible
    const [snackbarData, setSnackbarData] = useState<ISnackbarItem>({
        visible: false,
        text: '',
    });
    // indicates whether signed in and the authentication method used
    const [signedIn, setSignedIn] = useState<AUTHENTICATION_TYPE | null>(null);
    // stores cached value of user sound when tab or window not focused
    const [cachedHasSound, setCachedHasSound] = useState<boolean | null>(null);
    // indicates whether read page post banner is fixed to top of viewport
    const [stickyPostBannerActive, setStickyPostBannerActive] = useState<boolean>(false);
    // indicates whether notifications in NotificationDialog should be refreshed
    const [refreshNotifications, setRefreshNotifications] = useState<boolean>(true);
    // indicates whether buy buttons in reader view should be exposed
    const [queueRevealBuyButtons, setQueueRevealBuyButtons] = useState<boolean>(false);
    const lastAdminStatusSubjectRef = useRef<{
        userId: string | null,
        authenticationType: AUTHENTICATION_TYPE | null,
    }>({
        userId: null,
        authenticationType: null,
    });
    // deploy-preview troubleshooting panel for redirect/auth diagnostics.
    const [authDebugPanelEnabled, setAuthDebugPanelEnabled] = useState<boolean>(false);
    const [authDebugPanelExpanded, setAuthDebugPanelExpanded] = useState<boolean>(false);
    const [authDebugEntries, setAuthDebugEntries] = useState<ReturnType<typeof getAuthDebugLog>>([]);
    const [authDebugCopyStatus, setAuthDebugCopyStatus] = useState<string>('');

    useEffect(() => {
        const wasSigningInOut = previousSigningInOutRef.current;
        const wasSignedIn = previousSignedInRef.current;
        if (
            !wasSigningInOut
            && signingInOut
            && wasSignedIn != null
            && signOutToAnonymousStartedAtRef.current == null
        ) {
            signOutToAnonymousStartedAtRef.current = Date.now();
            signOutToAnonymousAuthReadyLoggedRef.current = false;
            signOutToAnonymousUserReadyLoggedRef.current = false;
            logAuthDebug('signout-to-anonymous-started', {
                authenticationType: wasSignedIn,
            });
        }
        previousSigningInOutRef.current = signingInOut;
        previousSignedInRef.current = signedIn;
    }, [signingInOut, signedIn]);

    const refreshAuthDebugEntries = useCallback((): void => {
        // Pull from shared auth debug buffer and keep only the newest entries
        // so panel rendering stays bounded even during noisy auth flows.
        const entries = getAuthDebugLog();
        setAuthDebugEntries(entries.slice(-AUTH_DEBUG_PANEL_MAX_ENTRIES));
    }, [AUTH_DEBUG_PANEL_MAX_ENTRIES]);

    useEffect(() => {
        // Re-evaluate panel visibility when URL query params change so
        // `?authDebug=1` can still be toggled without a full reload when the
        // env-based auth debug gate is not already enabled.
        if (typeof window === 'undefined') return;
        const enabled = isAuthDebugEnabled();
        setAuthDebugPanelEnabled(enabled);
        if (enabled) {
            // Auto-expand the panel and immediately hydrate it on enable.
            setAuthDebugPanelExpanded(true);
            refreshAuthDebugEntries();
            return;
        }
        setAuthDebugPanelExpanded(false);
    }, [location.search, refreshAuthDebugEntries]);

    useEffect(() => {
        if (!authDebugPanelEnabled || typeof window === 'undefined') return undefined;
        // Poll while open so async redirect/auth events appear without waiting
        // for unrelated React state changes.
        refreshAuthDebugEntries();
        const intervalId = window.setInterval(refreshAuthDebugEntries, AUTH_DEBUG_PANEL_POLL_INTERVAL);
        return () => {
            window.clearInterval(intervalId);
        };
    }, [
        authDebugPanelEnabled,
        refreshAuthDebugEntries,
        AUTH_DEBUG_PANEL_POLL_INTERVAL,
    ]);

    const onCopyAuthDebugLog = useCallback(async (): Promise<void> => {
        if (typeof window === 'undefined') return;
        // Export includes page/runtime context so copied logs are actionable
        // when shared from preview/dev environments.
        const exportPayload = JSON.stringify({
            href: window.location.href,
            userAgent: navigator.userAgent,
            ts: new Date().toISOString(),
            entries: authDebugEntries,
        }, null, 2);
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(exportPayload);
            } else {
                // Legacy clipboard fallback for environments without async clipboard API.
                const textArea = document.createElement('textarea');
                textArea.value = exportPayload;
                textArea.style.position = 'fixed';
                textArea.style.opacity = '0';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
            }
            setAuthDebugCopyStatus(`Copied ${authDebugEntries.length} auth log entries.`);
        } catch {
            setAuthDebugCopyStatus('Unable to copy auth logs.');
        }
    }, [authDebugEntries]);

    const onClearAuthDebugPanelLogs = useCallback((): void => {
        // Clear both global auth debug storage and current panel snapshot.
        clearAuthDebugLog();
        setAuthDebugEntries([]);
        setAuthDebugCopyStatus('Auth logs cleared.');
    }, []);

    const onDisableAuthDebugPanel = useCallback((): void => {
        // Session-only panel toggle. Permanent enablement is controlled by
        // development mode plus either the auth debug env flag or `?authDebug=1`.
        setAuthDebugPanelEnabled(false);
        setAuthDebugPanelExpanded(false);
        setAuthDebugEntries([]);
        setAuthDebugCopyStatus('');
    }, []);

    useEffect(() => {
        if (!authDebugCopyStatus || typeof window === 'undefined') return undefined;
        // Copy/clear status text is intentionally ephemeral to avoid stale UI state.
        const timeoutId = window.setTimeout(() => {
            setAuthDebugCopyStatus('');
        }, 2500);
        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [authDebugCopyStatus]);

    // Keep textarea rendering cheap by pre-formatting once per entry change.
    const authDebugEntriesOutput = useMemo(() => (
        JSON.stringify(authDebugEntries, null, 2)
    ), [authDebugEntries]);
    // Convenience summary for the panel header.
    const authDebugLastEvent = authDebugEntries.length > 0
        ? authDebugEntries[authDebugEntries.length - 1].event
        : null;
    // Expanded panel width is responsive but clamped for readability.
    const authDebugPanelWidth = authDebugPanelExpanded
        ? Math.max(180, Math.min(520, viewportDimensions.width - 32))
        : 220;

    const shouldShowPreviewDebugLogs = isTestMode();

    // Stores performance data to determine whether to show custom cursor or not
    const { off } = useHeavyComputeGate();

    // Auto-log visible failure snackbars once per route/message window so routes
    // that only call setSnackbarData still produce error telemetry.
    const snackbarRouteKey = `${location.pathname}${location.search}` || 'unknown-route';
    useFailureSnackbarLogger({
        test: isTestMode(),
        sessionId: currentSessionId,
        snackbarData,
        source: 'app-snackbar-auto',
        context: {
            signature: snackbarRouteKey,
            payload: {
                route: location.pathname,
            },
        },
        dedupeWindowMs: 2 * MILLISECONDS_IN_A_SECOND,
    });

    // ===== Cursor Constants =====

    const CURSOR_EXPAND_LENGTH = 3.75 * BODY_FONT_SIZE;
    const CURSOR_STANDARD_LENGTH = 1.25 * BODY_FONT_SIZE;
    const CURSOR_STANDARD_BORDER_WIDTH = 0.1875 * BODY_FONT_SIZE;
    const CURSOR_EXPAND_BORDER_WIDTH = 0.1875 * BODY_FONT_SIZE;
    const CURSOR_EXPAND_BACKGROUND = theme.verascopeColor.purple200;
    const CURSOR_TRANSITION_DURATION = DEFAULT_CSS_TRANSITION_DURATION / 2;
    const SMALL_CURSOR_ACTION_DELAY_DURATION = CURSOR_TRANSITION_DURATION / 2;
    const CURSOR_BORDER_LENGTH_TRANSITION_DURATION = DEFAULT_CSS_TRANSITION_DURATION;
    const CURSOR_EXPAND_BACKGROUND_TRANSLUCENCY = 0.9;
    const CURSOR_SIGN_ALTERNATE_DURATION = 2000;
    const CURSOR_LOCKED_PADDING = 0.3125 * BODY_FONT_SIZE;
    const CURSOR_LINE_WIDTH = 0.075 * BODY_FONT_SIZE;
    const SMALL_CURSOR_TRANSFORMATION_DELAY_DURATION = 100;
    const SMALL_CURSOR_ACTION_CONTAINER_LENGTH = 1.75 * BODY_FONT_SIZE;
    const SMALL_CURSOR_COMPUTER_CORNER_DELAY_DURATION = 50; // Lower than human visual perception

    // ===== Methods =====

    /**
     * Adjusts the position of the custom cursor to the position of the hidden cursor
     * when the hidden cursor moves
     * @param e mouse event
     */
    const modifyCursorPositionLatest = useLatest({
        customCursorActive,
        cursorLockedTargetProperties,
        cursorTarget,
        temporarilyUnlockCursor,
    });

    /**
     * Writes the custom cursor position once per animation frame so high-rate
     * mousemove streams do not force Safari to recalculate cursor styles for
     * every native mouse event.
     */
    const applyCursorPosition = (position: ICoord): void => {
        const cur = modifyCursorPositionLatest.current;
        if (!cur.customCursorActive) return;

        // Note that ref.current may be null. This is expected, because you may
        // conditionally render the ref-ed element, or you may forgot to assign it
        if (!customCursorRef.current) return;
        const hasCursorPosition = Number.isFinite(position.x) && Number.isFinite(position.y);
        if (!cur.cursorLockedTargetProperties && hasCursorPosition) {
            // Large Cursor
            customCursorRef.current.style.transform = `translate(calc(${position.x}px - 50%), calc(${position.y}px - 50%))`;
        } else if (cur.temporarilyUnlockCursor && cur.cursorTarget) {
            // Small Cursor with Grab
            const targetRect = cur.cursorTarget.getBoundingClientRect();
            const cursorX = targetRect.right - targetRect.width / 2;
            const cursorY = targetRect.bottom - targetRect.height / 2;
            customCursorRef.current.style.transform = `translate(calc(${cursorX}px - 50%), calc(${cursorY}px - 50%))`;
        } else if (cur.cursorLockedTargetProperties) {
            // Small Cursor
            customCursorRef.current.style.transform = `translate(${cur.cursorLockedTargetProperties.pos.x - CURSOR_LOCKED_PADDING}px, ${cur.cursorLockedTargetProperties.pos.y - CURSOR_LOCKED_PADDING}px)`;
        }

        if (smallCursorSightRef.current) {
            // little dot that's inside small cursor circle to tell you how where actual cursor is
            smallCursorSightRef.current.style.top = `${position.y}px`;
            smallCursorSightRef.current.style.left = `${position.x}px`;
        }
    };

    const flushCursorPosition = (): void => {
        cursorPositionAnimationFrameRef.current = null;
        if (!cursorMousePositionRef.current) return;

        applyCursorPosition(cursorMousePositionRef.current);
    };

    const modifyCursorPosition = (e: MouseEvent): void => {
        if (!modifyCursorPositionLatest.current.customCursorActive) return;

        cursorMousePositionRef.current = {
            x: e.pageX,
            y: e.pageY,
        };

        if (cursorPositionAnimationFrameRef.current != null) return;

        cursorPositionAnimationFrameRef.current = window.requestAnimationFrame(flushCursorPosition);
    };

    /**
     * Manages toggling sound when sound button clicked
     */
    const onSoundButtonClick = (): void => {
        const hadSound = hasSound;
        setHasSound(!hasSound);
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: hadSound
                    ? USER_ACTION_TYPE.turnOffSound
                    : USER_ACTION_TYPE.turnOnSound,
                sessionId: currentSessionId,
            });
        }
    };

    /**
     * Alters the presentation of the cursor to a larger form
     * when in contact with larger user interface items
     * @param cursor HTML element of custom cursor
     */
    const transformToLargeCursor = (cursor: HTMLDivElement): void => {
        // eslint-disable-next-line no-param-reassign
        cursor.style.borderColor = theme.color.neutral300;
        // eslint-disable-next-line no-param-reassign
        cursor.style.borderRadius = '100%';
        // eslint-disable-next-line no-param-reassign
        cursor.style.borderWidth = `${CURSOR_EXPAND_BORDER_WIDTH}px`;
        // eslint-disable-next-line no-param-reassign
        cursor.style.width = `${CURSOR_EXPAND_LENGTH}px`;
        // eslint-disable-next-line no-param-reassign
        cursor.style.height = `${CURSOR_EXPAND_LENGTH}px`;
    };

    /**
     * Alters the presentation of the cursor to a smaller form
     * when in contact with smaller user interface items
     * @param cursor HTML element of custom cursor
     * @param target HTML element of cursor target
     * @param rect DOM Rect of cursor target
     */
    const transformToSmallCursor = (
        cursor: HTMLDivElement,
        target: HTMLElement,
        rect: DOMRect,
    ): void => {
        // Change cursor ring color if target background is white
        if (shouldUseDarkCursor(target.parentElement as HTMLElement)) {
            // eslint-disable-next-line no-param-reassign
            cursor.style.borderColor = theme.verascopeColor.purple200;
        } else {
            // eslint-disable-next-line no-param-reassign
            cursor.style.borderColor = theme.color.white;
        }

        // Change cursor sight color if target is white
        if (smallCursorSightRef.current) {
            if (shouldUseDarkCursor(target as HTMLElement)) {
                smallCursorSightRef.current.style.background = theme.verascopeColor.purple200;
            } else {
                smallCursorSightRef.current.style.background = theme.color.white;
            }
        }
        // eslint-disable-next-line no-param-reassign
        cursor.style.borderWidth = `${CURSOR_EXPAND_BORDER_WIDTH}px`;
        // eslint-disable-next-line no-param-reassign
        cursor.style.width = `${rect.width + 2 * CURSOR_LOCKED_PADDING}px`;
        // eslint-disable-next-line no-param-reassign
        cursor.style.height = `${rect.height + 2 * CURSOR_LOCKED_PADDING}px`;

        // Initiate Cursor Inertia
        setCursorLockedTargetProperties({
            width: rect.width,
            height: rect.height,
            pos: {
                x: rect.x,
                y: rect.y,
            },
        });

        // Note that ref.current may be null. This is expected, because you may
        // conditionally render the ref-ed element, or you may forgot to assign it
        if (!customCursorRef.current) return;

        clearTimeoutComputeSmallCursorCorner();
        timeoutComputeSmallCursorCorner();
    };

    /**
     * Alters the presentation of the cursor to a line form
     * when in contact with text box elements
     * @param cursor HTML element of custom cursor
     * @param target HTML element of cursor target
     * @param _rect DOM Rect of cursor target
     */
    const transformToLineCursor = (
        cursor: HTMLDivElement,
        target: HTMLElement,
        _rect: DOMRect,
        dark = true,
    ): void => {
        const targetFontSize = window
            .getComputedStyle(target, null)
            .getPropertyValue('line-height');
        const cursorFontSize = `${1.5 * parseInt(targetFontSize.replace('px', ''), 10)}px`;
        // Target is an input. Use a dark or light line cursor variant based on context.
        // eslint-disable-next-line no-param-reassign
        cursor.style.borderColor = dark
            ? theme.verascopeColor.purple200
            : theme.color.white;
        // eslint-disable-next-line no-param-reassign
        cursor.style.borderWidth = `${CURSOR_LINE_WIDTH}px`;
        // eslint-disable-next-line no-param-reassign
        cursor.style.borderRadius = '1px';
        // eslint-disable-next-line no-param-reassign
        cursor.style.width = '0px';
        // eslint-disable-next-line no-param-reassign
        cursor.style.height = cursorFontSize;
    };

    /**
     * Manages response to custom cursor hovering on a user interface element
     * @param targetType The kind of user interface element in focus
     * @param actions The list of (cursor) actions possible on user interface element
     * @param candidateTarget HTML element of cursor target
     */
    const onCursorEnter = useCallback((
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ): void => {
        // IMPORTANT: This method should never be triggered when an interactable object is grabbed
        // We avoid triggering this method when an object is grabbed in LandingView

        if (!customCursorActive) return;

        // We can transition directly between hover targets without requiring
        // an intermediate mouseleave event from the previous target.
        if (cursorType !== CURSOR_TYPE.resting) {
            setCursorLockedTargetProperties(null);
            clearTimeoutCursorSmallTransform();
            if (transitioningToSmallCursor) setTransitioningToSmallCursor(false);
        }

        // Note that ref.current may be null. This is expected, because you may
        // conditionally render the ref-ed element, or you may forgot to assign it
        if (!customCursorRef.current) return;

        if (candidateTarget) {
            const target = findParentNodeWithClass(HOVER_TARGET_CLASSNAME, candidateTarget, 5);
            if (!target) return;
            const targetRect = target.getBoundingClientRect();
            const cursorTarget = targetType as CURSOR_TARGET;
            if (
                cursorTarget === CURSOR_TARGET.input
                || cursorTarget === CURSOR_TARGET.editor
                || cursorTarget === CURSOR_TARGET.annotator
            ) {
                transformToLineCursor(
                    customCursorRef.current,
                    target,
                    targetRect,
                    !routeIsAuthActionPage,
                );
                setCursorType(CURSOR_TYPE.line);
            } else if (
                targetRect.width < CURSOR_EXPAND_LENGTH
                && targetRect.height < CURSOR_EXPAND_LENGTH
            ) {
                setTransitioningToSmallCursor(true);
                clearTimeoutCursorSmallTransform();
                timeoutCursorSmallTransform({
                    cursor: customCursorRef.current,
                    rect: targetRect,
                    target,
                });
            } else {
                transformToLargeCursor(customCursorRef.current);
                setCursorType(CURSOR_TYPE.engagedLarge);
            }
        } else {
            transformToLargeCursor(customCursorRef.current);
            setCursorType(CURSOR_TYPE.engagedLarge);
        }

        setCursorSigns([...actions]);
        setCursorTargetType(targetType as CURSOR_TARGET);
        setCursorTarget(candidateTarget);
    }, [
        cursorType,
        customCursorActive,
        routeIsAuthActionPage,
        transitioningToSmallCursor,
    ]);

    /**
     * Manages response to custom cursor no longer hovering on a user interface element
     * @param e mouse event
     */
    const onCursorLeave = useCallback((e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent): void => {
        // IMPORTANT: This method should never be triggered when an interactable object is grabbed
        // We avoid triggering this method when an object is grabbed in

        // Note that ref.current may be null. This is expected, because you may
        // conditionally render the ref-ed element, or you may forgot to assign it
        if (!customCursorActive || !customCursorRef.current) return;

        const mouseEvent = e as React.MouseEvent | undefined;

        // Moved early in function so that modifyCursorPosition factors it as soon as possible
        // Causes jumping cursor otherwise
        setCursorLockedTargetProperties(null);

        clearTimeoutCursorSmallTransform();
        if (transitioningToSmallCursor) setTransitioningToSmallCursor(false);

        customCursorRef.current.style.borderColor = theme.color.neutral300;
        customCursorRef.current.style.borderWidth = `${CURSOR_STANDARD_BORDER_WIDTH}px`;
        customCursorRef.current.style.borderRadius = '100%';
        customCursorRef.current.style.width = `${CURSOR_STANDARD_LENGTH}px`;
        customCursorRef.current.style.height = `${CURSOR_STANDARD_LENGTH}px`;
        if (
            mouseEvent
            && Number.isFinite(mouseEvent.pageX)
            && Number.isFinite(mouseEvent.pageY)
        ) {
            customCursorRef.current.style.transform = `translate(calc(${mouseEvent.pageX}px - 50%), calc(${mouseEvent.pageY}px - 50%))`;
        }

        // Needs to be before initiating onCursorEnter
        // because that method prevents non-resting cursors from executing code
        setCursorType(CURSOR_TYPE.resting);

        if (cursorSigns.length > 0) setCursorSigns([]);
        // We don't check for cursorTargetType because we want to be sure its clear
        // There is an input bug in the ReaderView where the cursorTargetType is not cleared
        // which causes the cursor to be in the incorrect state
        setCursorTargetType(null);
        if (cursorTarget) setCursorTarget(undefined);
        if (
            smallCursorCorner.top !== DEFAULT_SMALL_CURSOR_CORNER.top
            || smallCursorCorner.left !== DEFAULT_SMALL_CURSOR_CORNER.left
        ) {
            setSmallCursorCorner(DEFAULT_SMALL_CURSOR_CORNER);
        }
    }, [
        transitioningToSmallCursor,
        cursorSigns,
        cursorTarget,
        smallCursorCorner,
        customCursorActive,
    ]);

    /**
     * Manages response to custom cursor selecting on a user interface element
     * @param e mouse event
     */
    const onCursorMouseDown = (): void => {
        if (!customCursorActive) return;
        // Note that ref.current may be null. This is expected, because you may
        // conditionally render the ref-ed element, or you may forgot to assign it
        if (!customCursorRef.current) return;

        setCheckUnlockCursor(true);
    };

    /**
     * Manages response to custom cursor de-selecting on a user interface element
     * @param e mouse event
     */
    const onCursorMouseUp = (): void => {
        if (!customCursorActive || !customCursorRef.current) return;

        setCheckUnlockCursor(true);
    };

    /**
     * Manages any modifications that should apply due to viewport changes
     */
    const handleResize = (): void => {
        // Update viewport dimensions
        setViewportDimensions({
            width: Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0),
            height: Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0),
        });
    };

    const USER_REFRESH_TTL_MS = 3 * MILLISECONDS_IN_A_SECOND;
    type IUserFetchCacheEntry = {
        expiresAt: number,
        inFlight: Promise<unknown> | null,
        value: unknown,
    };
    // Global per-tab dedupe + short TTL cache for user/profile/admin read calls.
    const userFetchCacheRef = useRef<Map<string, IUserFetchCacheEntry>>(new Map());
    function requestUserDataWithCache<T>(
        key: string,
        fetcher: () => Promise<T>,
        force = false,
    ): Promise<T> {
        const now = Date.now();
        const cached = userFetchCacheRef.current.get(key);
        if (
            !force
            && cached != null
            && cached.expiresAt > now
            && cached.inFlight == null
        ) {
            return Promise.resolve(cached.value as T);
        }
        // Force refresh should bypass stale cached values, but if an identical refresh
        // is already in flight (for example from both auth listeners during sign-in),
        // reuse that promise instead of starting duplicate work.
        if (cached?.inFlight != null) {
            return cached.inFlight as Promise<T>;
        }
        const request = fetcher().then((value) => {
            userFetchCacheRef.current.set(key, {
                expiresAt: Date.now() + USER_REFRESH_TTL_MS,
                inFlight: null,
                value,
            });
            return value;
        }).catch((err) => {
            const stale = userFetchCacheRef.current.get(key);
            if (stale != null) {
                userFetchCacheRef.current.set(key, {
                    ...stale,
                    inFlight: null,
                });
            }
            throw err;
        });
        userFetchCacheRef.current.set(key, {
            expiresAt: cached?.expiresAt || 0,
            inFlight: request,
            value: cached?.value,
        });
        return request;
    }
    const requestUserData = (
        userId: string,
        force = false,
    ): Promise<IPartialUserItem | null> => requestUserDataWithCache(
        `userData:${userId}`,
        () => getUser({
            test: isTestMode(),
            userId,
        }),
        force,
    );
    // Use these wrappers on known mutation paths (or ping bumps) to bypass TTL.
    const refreshUserDataNow = (userId: string): Promise<IPartialUserItem | null> => (
        requestUserData(userId, true)
    );
    const refreshUserEmailNow = (userId: string): Promise<string | null> => (
        requestUserDataWithCache(
            `userEmail:${userId}`,
            () => getUserEmail({
                test: isTestMode(),
            }),
            true,
        )
    );
    const refreshUserAdminStatusNow = (userId: string): Promise<boolean> => (
        requestUserDataWithCache(
            `userAdminStatus:${userId}`,
            () => getUserAdminStatus({
                test: isTestMode(),
            }),
            true,
        )
    );
    const refreshUserPostAccessNow = (userId: string): Promise<Map<string, IPostAccessItem> | null> => (
        requestUserDataWithCache(
            `userPostAccess:${userId}`,
            () => getUserPostAccess({
                test: isTestMode(),
            }),
            true,
        )
    );
    const refreshUserCartItemsNow = (userId: string): Promise<ICartItem[] | null> => (
        requestUserDataWithCache(
            `userCartItems:${userId}`,
            () => getUserCartItems({
                test: isTestMode(),
            }),
            true,
        )
    );
    const setUserCartItemsWithSyncGuard: React.Dispatch<React.SetStateAction<ICartItem[] | undefined>> = useCallback((value) => {
        setUserCartItems((prev) => {
            const next = typeof value === 'function'
                ? value(prev)
                : value;
            // Keep this guard short; it only blocks stale in-flight snapshots right after local cart edits.
            const localGuardState = getLocalCartSyncGuardState();
            userCartLocalFreshnessRef.current = localGuardState.localFreshness;
            userCartLocalGuardUntilRef.current = localGuardState.localGuardUntil;
            return next;
        });
    }, []);
    const getEmailVerifiedFromAuthOrUserItem = useCallback((userItem: IPartialUserItem): boolean => {
        const authUser = auth.currentUser;
        const authUserEmailVerified = !!(
            authUser
            && !authUser.isAnonymous
            && authUser.uid === userItem.id
            && authUser.emailVerified
        );
        if (authUserEmailVerified) return true;
        return (
            userItem.authenticationType === AUTHENTICATION_TYPE.email
            && !!userItem.emailVerified
        );
    }, []);
    const syncUserEmailVerifiedFromUserItem = useCallback((userItem: IPartialUserItem): void => {
        if (userItem.authenticationType !== AUTHENTICATION_TYPE.email) return;
        setUserEmailVerified(getEmailVerifiedFromAuthOrUserItem(userItem));
    }, [getEmailVerifiedFromAuthOrUserItem]);
    const logUserData = ({
        userItem,
    }: { userItem: IPartialUserItem }): void => {
        // Keep user-payload diagnostics behind their own dev-only gate so they
        // can be enabled without the rest of auth debug output.
        logAuthUser('app-user-data-refreshed', {
            userId: userItem.id,
            authenticationType: userItem.authenticationType,
            hasPhotoURL: !!userItem.photoURL,
            emailVerified: userItem.emailVerified,
            customCursorEnabled: userItem.customCursorEnabled,
            saveReaderProgress: userItem.saveReaderProgress,
            readerChapterProgress: userItem.readerChapterProgress,
        });
    };
    const fetchUserData = (
        userId: string,
        force = false,
    ): void => {
        const request = force
            ? refreshUserDataNow(userId)
            : requestUserData(userId, false);
        request.then((userItem) => {
            if (userItem != null) {
                syncUserEmailVerifiedFromUserItem(userItem);
                setUser((prev) => {
                    // Avoid re-rendering the entire signed-in app tree when the
                    // refreshed user payload is structurally identical.
                    if (deepEqual(prev, userItem)) {
                        return prev;
                    }
                    logUserData({ userItem });
                    return {
                        ...userItem,
                    };
                });
            }
        }).catch(() => null);
    };

    /**
     * Fetch user email
     */
    const fetchUserEmailLatest = useLatest({
        userEmail,
        fetchingUserEmail,
    });
    const fetchUserEmail = (
        userId: string,
        force = false,
    ): void => {
        if (!fetchingUserEmail) setFetchingUserEmail(true);
        // Passive reads rely on short TTL + in-flight dedupe. Force only on known writes.
        const request = force
            ? refreshUserEmailNow(userId)
            : requestUserDataWithCache(
                `userEmail:${userId}`,
                () => getUserEmail({
                    test: isTestMode(),
                }),
                false,
            );
        request.then((email) => {
            const cur = fetchUserEmailLatest.current;
            if (email !== cur.userEmail) setUserEmail(email);
        }).catch(() => null).finally(() => {
            const cur = fetchUserEmailLatest.current;
            if (cur.fetchingUserEmail) setFetchingUserEmail(false);
        });
    };

    /**
     * Fetch user admin status
     */
    const fetchUserAdminStatusLatest = useLatest({
        userIsAdmin,
        fetchingUserAdminStatus,
        fetchedUserAdminStatus,
    });
    const fetchUserAdminStatus = (
        userId: string,
        force = false,
    ): void => {
        if (!fetchingUserAdminStatus) setFetchingUserAdminStatus(true);
        // Passive reads rely on short TTL + in-flight dedupe. Force only on known writes.
        const request = force
            ? refreshUserAdminStatusNow(userId)
            : requestUserDataWithCache(
                `userAdminStatus:${userId}`,
                () => getUserAdminStatus({
                    test: isTestMode(),
                }),
                false,
            );
        request.then((isAdmin) => {
            const cur = fetchUserAdminStatusLatest.current;
            if (isAdmin !== cur.userIsAdmin) setUserIsAdmin(isAdmin);
            if (!cur.fetchedUserAdminStatus) {
                setFetchedUserAdminStatus(true);
            }
        }).catch(() => null).finally(() => {
            const cur = fetchUserAdminStatusLatest.current;
            if (cur.fetchingUserAdminStatus) setFetchingUserAdminStatus(false);
        });
    };

    /**
     * Fetch user post access
     */
    const fetchUserPostAccessLatest = useLatest({
        userPostAccess,
        fetchingUserPostAccess,
    });
    const fetchUserPostAccess = (
        userId: string,
        force = false,
    ): void => {
        if (!fetchingUserPostAccess) setFetchingUserPostAccess(true);
        // Passive reads rely on short TTL + in-flight dedupe. Force only on known writes.
        const request = force
            ? refreshUserPostAccessNow(userId)
            : requestUserDataWithCache(
                `userPostAccess:${userId}`,
                () => getUserPostAccess({
                    test: isTestMode(),
                }),
                false,
            );
        request.then((postAccessMap) => {
            const cur = fetchUserPostAccessLatest.current;
            const normalizedCurrentPostAccess = cur.userPostAccess
                ? Array.from(cur.userPostAccess.entries())
                : null;
            const normalizedIncomingPostAccess = postAccessMap
                ? Array.from(postAccessMap.entries())
                : null;

            if (!deepEqual(normalizedCurrentPostAccess, normalizedIncomingPostAccess)) {
                setUserPostAccess(postAccessMap || undefined);
            }
        }).catch(() => null).finally(() => {
            const cur = fetchUserPostAccessLatest.current;
            if (cur.fetchingUserPostAccess) setFetchingUserPostAccess(false);
        });
    };

    /**
     * Fetch user cart items
     */
    const fetchUserCartItemsLatest = useLatest({
        fetchingUserCartItems,
        userCartItems,
    });
    const fetchUserCartItems = (
        userId: string,
        force = false,
    ): void => {
        if (!fetchingUserCartItems) setFetchingUserCartItems(true);
        // Passive reads rely on short TTL + in-flight dedupe. Force only on known writes.
        const request = force
            ? refreshUserCartItemsNow(userId)
            : requestUserDataWithCache(
                `userCartItems:${userId}`,
                () => getUserCartItems({
                    test: isTestMode(),
                }),
                false,
            );
        request.then((cartItems) => {
            const cur = fetchUserCartItemsLatest.current;
            const normalizedCartItems = cartItems || undefined;
            // If we recently changed cart locally and the incoming snapshot is older,
            // ignore it to avoid UI "snap-back" during rapid quantity changes.
            const shouldBlockStaleIncomingCart = shouldBlockIncomingCartSnapshot({
                incomingCartItems: normalizedCartItems,
                localFreshness: userCartLocalFreshnessRef.current,
                localGuardUntil: userCartLocalGuardUntilRef.current,
            });
            if (shouldBlockStaleIncomingCart) {
                return;
            }
            if (!deepEqual(cur.userCartItems, normalizedCartItems)) {
                setUserCartItems(normalizedCartItems);
            }
        }).catch(() => null).finally(() => {
            const cur = fetchUserCartItemsLatest.current;
            if (cur.fetchingUserCartItems) setFetchingUserCartItems(false);
        });
    };

    /**
     * Initiates a user session upon app startup
     * @param sessionId unique identifier of current session
     */
    const startSessionLatest = useLatest({
        startingUserSession,
        browserTabIsFocused,
        userId: user?.id || null,
    });
    const waitForMilliseconds = (duration: number): Promise<void> => new Promise((resolve) => {
        setTimeout(resolve, duration);
    });
    /**
     * Avoids attaching a session with a Firebase token for a user whose Firestore
     * document is not the same user React is currently rendering.
     */
    const abortSessionStartForAuthMismatch = (): boolean => {
        const cur = startSessionLatest.current;
        const currentAuthUserId = auth.currentUser?.uid || null;
        if (
            cur.userId != null
            && cur.userId === currentAuthUserId
        ) {
            return false;
        }

        logAuthDebug('start-session-skipped-auth-user-mismatch', {
            currentAuthUserId,
            userId: cur.userId,
        });
        if (cur.startingUserSession) setStartingUserSession(false);
        return true;
    };
    const isUserMissingError = (error: unknown): boolean => {
        const errorCode = (error as { code?: string } | undefined)?.code || '';
        const errorMessage = (error as { message?: string } | undefined)?.message || '';
        return (
            errorCode === 'functions/not-found'
            || errorMessage.includes('User doesn\'t exist')
            || errorMessage.includes("User doesn't exist")
        );
    };
    const attachSessionToUserWithRetry = async (sessionId: string): Promise<void> => {
        for (let i = 0; i < USER_SESSION_ATTACH_RETRY_DELAYS_MS.length; i += 1) {
            if (USER_SESSION_ATTACH_RETRY_DELAYS_MS[i] > 0) {
                // eslint-disable-next-line no-await-in-loop
                await waitForMilliseconds(USER_SESSION_ATTACH_RETRY_DELAYS_MS[i]);
            }
            try {
                // eslint-disable-next-line no-await-in-loop
                await updateUser({
                    test: isTestMode(),
                    sessionsToAdd: [sessionId],
                });
                return;
            } catch (error) {
                const isLastAttempt = i === USER_SESSION_ATTACH_RETRY_DELAYS_MS.length - 1;
                if (
                    !isUserMissingError(error)
                    || isLastAttempt
                ) {
                    throw error;
                }
            }
        }
    };
    const startSession = async (): Promise<void> => {
        try {
            if (abortSessionStartForAuthMismatch()) return;
            // Creat Session
            const ip = await getUserIPInfo();
            if (abortSessionStartForAuthMismatch()) return;
            const sessionId = await startUserSession({
                test: isTestMode(),
                browser: detectBrowser(),
                isMobile: isMobile(),
                isMacOS: isMacOS(),
                ip,
            });
            let shouldSetCurrentSessionId = true;
            try {
                await attachSessionToUserWithRetry(sessionId);
            } catch (error) {
                const isMissingUser = isUserMissingError(error);
                logAuthDebug('start-session-user-attach-failed', {
                    error: (error as { message?: string } | undefined)?.message || String(error),
                    missingUser: isMissingUser,
                });
                // If user doc is still not visible, avoid exposing session-dependent
                // reads/writes yet. The initialization effect will retry shortly.
                if (isMissingUser) {
                    shouldSetCurrentSessionId = false;
                }
            }

            const cur = startSessionLatest.current;
            if (shouldSetCurrentSessionId) {
                // We put setCurrentSessionLastActivityStartTimestamp first because
                // it doesn't trigger any side effects
                // But setCurrentSessionId does, which may assume a most recent
                // value for currentSessionTimestamp
                if (cur.browserTabIsFocused != null) {
                    setCurrentSessionLastActivityStartTimestamp(cur.browserTabIsFocused);
                } else {
                    setCurrentSessionLastActivityStartTimestamp(Date.now());
                }
                setCurrentSessionId(sessionId);
            }
            if (cur.startingUserSession) setStartingUserSession(false);
        } catch {
            const cur = startSessionLatest.current;
            if (cur.startingUserSession) setStartingUserSession(false);
        }
    };

    const logFocusElapsed = (source: 'tab' | 'window', focusedAt: number | null): void => {
        const activeFocusStartTimestamp = focusStartTimestampRef.current ?? focusedAt;
        if (activeFocusStartTimestamp == null) return;

        const elapsedMs = Math.max(0, Date.now() - activeFocusStartTimestamp);
        totalFocusedDurationRef.current += elapsedMs;
        focusStartTimestampRef.current = null;

        if (shouldShowPreviewDebugLogs) {
            const elapsedSeconds = (elapsedMs / 1000).toFixed(2);
            const totalElapsedSeconds = (totalFocusedDurationRef.current / 1000).toFixed(2);
            // Debug timing diagnostics for tab/window exits (dev + preview debug flag).
            console.log(`[focus] ${source} exit: elapsed=${elapsedMs}ms (${elapsedSeconds}s), total=${totalFocusedDurationRef.current}ms (${totalElapsedSeconds}s)`);
        }
    };

    const syncBrowserFocusState = (): void => {
        const cur = handleTabChangeLatest.current;
        const pageIsActive = document.visibilityState === 'visible' && document.hasFocus();
        if (pageIsActive) {
            if (cur.cachedHasSound !== null) {
                setHasSound(cur.cachedHasSound);
                setCachedHasSound(null);
            }
            if (cur.browserTabIsFocused == null) {
                const focusTimestamp = Date.now();
                focusStartTimestampRef.current = focusTimestamp;
                setBrowserTabIsFocused(focusTimestamp);
            }
        } else {
            if (document.visibilityState !== 'visible') {
                logFocusElapsed('tab', cur.browserTabIsFocused);
            }
            setCachedHasSound(cur.hasSound);
            setHasSound(false);
            if (cur.browserTabIsFocused != null) {
                focusStartTimestampRef.current = null;
                setBrowserTabIsFocused(null);
            }
        }
    };

    /**
     * Manages toggling sound on and off and setting a tab focus flag
     * based on whether tab is focused
     */
    const handleTabChangeLatest = useLatest({
        hasSound,
        cachedHasSound,
        browserTabIsFocused,
    });
    const handleTabChange = (): void => {
        syncBrowserFocusState();
    };

    /**
     * Manages toggling sound on and off and setting a tab focus flag
     * based on whether window is focused
     * @param focused whether window is focused or blurred
     */
    const handleWindowChangeLatest = useLatest({
        hasSound,
        cachedHasSound,
        browserTabIsFocused,
    });
    const handleWindowChange = (focused: boolean): void => {
        // Keep blur immediate; on focus, verify tab visibility + document focus.
        if (!focused) {
            const cur = handleWindowChangeLatest.current;
            logFocusElapsed('window', cur.browserTabIsFocused);
            setCachedHasSound(cur.hasSound);
            setHasSound(false);
            if (cur.browserTabIsFocused != null) {
                focusStartTimestampRef.current = null;
                setBrowserTabIsFocused(null);
            }
            return;
        }

        syncBrowserFocusState();
    };

    const onUserProfileDialogIsExpanded = (isExpanded: boolean): void => {
        setUserProfileDialogExpanded(isExpanded);

        if (notificationDialogExpanded) {
            setNotificationDialogExpanded(false);
        }
    };

    const onNotificationDialogIsExpanded = (isExpanded: boolean): void => {
        setNotificationDialogExpanded(isExpanded);

        if (userProfileDialogExpanded) {
            setUserProfileDialogExpanded(false);
        }
    };

    const restoreState = async (): Promise<void> => {
        // eslint-disable-next-line no-underscore-dangle
        const result = (window as any).__redirectResult as import('firebase/auth').UserCredential | null;
        // eslint-disable-next-line no-underscore-dangle
        const error  = (window as any).__redirectError as any;
        // eslint-disable-next-line no-underscore-dangle
        (window as any).__redirectResult = null;
        // eslint-disable-next-line no-underscore-dangle
        (window as any).__redirectError  = null;
        logAuthDebug('app-restore-state-read', {
            hasResult: !!result,
            resultProviderId: result?.providerId || null,
            resultUserId: result?.user?.uid || null,
            hasError: !!error,
            errorCode: error?.code || null,
            errorMessage: error?.message || null,
        });

        if (error) {
            logAuthDebug('app-restore-state-error', {
                code: error?.code || null,
                message: error?.message || null,
            });
            clearRedirectFlag();
            setRedirectStateChecked(true);
            return;
        }

        if (result) {
            const { accessToken, providerId } = extractCredential(result);
            const restored = restoreStateAfterRedirect();
            logAuthDebug('app-restore-state-result-branch', {
                providerId,
                hasOldUserData: !!restored?.oldUserData,
                hasSigningInOut: typeof restored?.signingInOut === 'boolean',
                hasHasSound: typeof restored?.hasSound === 'boolean',
                hasScrollY: typeof restored?.scrollY === 'number',
            });
            if (typeof restored?.signingInOut === 'boolean') {
                setSigningInOut(restored.signingInOut);
            }
            if (typeof restored?.hasSound === 'boolean') {
                setHasSound(restored.hasSound);
            }
            if (typeof restored?.scrollY === 'number') {
                window.scrollTo(0, restored.scrollY);
            }
            // Save again so that it can be read by UserProfileDialog
            saveStateForRedirect({
                auth: {
                    user: result.user?.toJSON?.() ?? result.user,
                    accessToken,
                    providerId,
                },
                oldUserData: restored?.oldUserData,
            });
            logAuthDebug('app-restore-state-resaved', {
                providerId,
                hasOldUserData: !!restored?.oldUserData,
            });
            clearRedirectFlag();
            setRedirectStateChecked(true);
        }
    };

    const clearRedirectFlag = (): void => {
        try {
            sessionStorage.removeItem('vs_redirecting');
        } catch { /* ignore */ }
        try {
            localStorage.removeItem('vs_redirecting');
        } catch { /* ignore */ }
        logAuthDebug('app-redirect-flag-cleared');
    };

    // ===== Side Effects =====

    const routeBackground = useMemo<string>(() => {
        const normalizedPath = location.pathname.replace(/^\/+/, '');

        if (normalizedPath === PAGE_ROUTE.about) {
            return theme.verascopeColor.orange100;
        }

        if (
            normalizedPath === PAGE_ROUTE.privacyPolicy
            || normalizedPath === PAGE_ROUTE.termsOfUse
            || normalizedPath === PAGE_ROUTE.physicalBook
            || normalizedPath === PAGE_ROUTE.cart
            || normalizedPath.startsWith(`${PAGE_ROUTE.checkout}/`)
            || normalizedPath === PAGE_ROUTE.authAction
        ) {
            return theme.color.white;
        }

        if (
            normalizedPath === PAGE_ROUTE.book
            || normalizedPath.startsWith(`${PAGE_ROUTE.book}/`)
        ) {
            return theme.color.cream500;
        }

        if (normalizedPath === PAGE_ROUTE.characters) {
            return viewportDimensions.width <= 991
                ? theme.verascopeColor.purple200
                : theme.verascopeColor.purple100;
        }

        if (
            normalizedPath === PAGE_ROUTE.treasureChest
            || normalizedPath.startsWith(`${PAGE_ROUTE.treasureChest}/`)
        ) {
            return theme.verascopeColor.blue200;
        }

        if (normalizedPath === PAGE_ROUTE.socialEmergence) {
            return theme.color.offWhite;
        }

        if (normalizedPath === PAGE_ROUTE.landing) {
            return theme.verascopeColor.purple200;
        }

        return theme.verascopeColor.purple200;
    }, [
        location.pathname,
        viewportDimensions.width,
    ]);

    /**
     * Keeps the document-level background in sync with the active route
     * so iOS safe-area and overscroll reveal the same color as the page.
     */
    useEffect(() => {
        document.documentElement.style.setProperty('--page-bg', routeBackground);
    }, [routeBackground]);

    /**
     * Manages event listener that tracks on focus and on blur events of window (incl. browser console)
     * Detects when *window* focus changes (incl. browser console)
     */
    useEffect(() => {
        const handleFocus = (): void => handleWindowChange(true);
        const handleBlur = (): void => handleWindowChange(false);
        window.addEventListener('focus', handleFocus);
        window.addEventListener('blur', handleBlur);

        return function cleanup() {
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('blur', handleBlur);
        };
    }, [
        cachedHasSound,
        browserTabIsFocused,
    ]);

    /**
     * Manages event listener that tracks on focus and on blur events of browser tab
     * Detects when browser tab is switched
     */
    useEffect(() => {
        document.addEventListener('visibilitychange', handleTabChange, false);

        return function cleanup() {
            document.removeEventListener('visibilitychange', handleTabChange, false);
        };
    }, [
        cachedHasSound,
        browserTabIsFocused,
    ]);

    /**
     * Enables document scrolling on touch devices so Safari browser chrome can collapse.
     */
    useEffect(() => {
        const allowDocumentScroll = !customCursorActive;
        document.body.style.overflowX = 'hidden';
        document.body.style.overflowY = allowDocumentScroll ? 'auto' : 'hidden';
        document.documentElement.style.overflowY = allowDocumentScroll ? 'auto' : 'hidden';

        return function cleanup() {
            document.body.style.overflowX = '';
            document.body.style.overflowY = '';
            document.documentElement.style.overflowY = '';
        };
    }, [customCursorActive]);

    /**
     * Manages restoring critical auth state lost when redirecting on sign-in
     */
    useEffect(() => {
        (async () => {
            logAuthDebug('app-redirect-restore-effect-started', {
                hadPendingRedirect,
            });
            // Ensure redirect result is settled
            await redirectReady?.catch(() => null);
            logAuthDebug('app-redirect-ready-settled');

            const u = auth.currentUser;
            if (u && !u.isAnonymous) {
                logAuthDebug('app-redirect-current-user-authenticated-pre-restore', {
                    userId: u.uid,
                    providerId: u.providerData?.[0]?.providerId || null,
                });
                // Try to grab an ID token (always available) to carry forward
                const idToken = await u.getIdToken().catch(() => null);
                const providerId = u.providerData?.[0]?.providerId ?? 'firebase';

                // Mirror what you do in restoreState(result) but with a synthetic payload
                const restored = restoreStateAfterRedirect();
                logAuthDebug('app-redirect-pre-restore-state', {
                    providerId,
                    hasOldUserData: !!restored?.oldUserData,
                });
                if (typeof restored?.signingInOut === 'boolean') setSigningInOut(restored.signingInOut);
                if (typeof restored?.hasSound === 'boolean') setHasSound(restored.hasSound);
                if (typeof restored?.scrollY === 'number') window.scrollTo(0, restored.scrollY);

                // Make the minimal auth blob other parts of your app expect
                saveStateForRedirect({
                    auth: {
                        user: u.toJSON?.() ?? u,
                        // Use ID token as a stand-in when the provider access token isn't returned
                        accessToken: idToken ?? undefined,
                        providerId,
                    },
                    oldUserData: restored?.oldUserData,
                });
                logAuthDebug('app-redirect-pre-restore-saved-handoff', {
                    providerId,
                    hasOldUserData: !!restored?.oldUserData,
                    hasIdToken: !!idToken,
                });

                clearRedirectFlag();
                setRedirectStateChecked(true);
                return; // don't call restoreState() again
            }
            // Now it's safe to read window.__redirectResult in your restoreState()
            await restoreState();
            logAuthDebug('app-redirect-after-restore-state-call');

            const u2 = auth.currentUser;
            if (u2 && !u2.isAnonymous) {
                logAuthDebug('app-redirect-current-user-authenticated-post-restore', {
                    userId: u2.uid,
                    providerId: u2.providerData?.[0]?.providerId || null,
                });
                // Try to grab an ID token (always available) to carry forward
                const idToken = await u2.getIdToken().catch(() => null);
                const providerId = u2.providerData?.[0]?.providerId ?? 'firebase';

                // Mirror what you do in restoreState(result) but with a synthetic payload
                const restored = restoreStateAfterRedirect();
                logAuthDebug('app-redirect-post-restore-state', {
                    providerId,
                    hasOldUserData: !!restored?.oldUserData,
                });
                if (typeof restored?.signingInOut === 'boolean') setSigningInOut(restored.signingInOut);
                if (typeof restored?.hasSound === 'boolean') setHasSound(restored.hasSound);
                if (typeof restored?.scrollY === 'number') window.scrollTo(0, restored.scrollY);

                // Make the minimal auth blob other parts of your app expect
                saveStateForRedirect({
                    auth: {
                        user: u2.toJSON?.() ?? u,
                        // Use ID token as a stand-in when the provider access token isn't returned
                        accessToken: idToken ?? undefined,
                        providerId,
                    },
                    oldUserData: restored?.oldUserData,
                });
                logAuthDebug('app-redirect-post-restore-saved-handoff', {
                    providerId,
                    hasOldUserData: !!restored?.oldUserData,
                    hasIdToken: !!idToken,
                });

                clearRedirectFlag();
                setRedirectStateChecked(true);
                return; // don't call restoreState() again
            }

            // If we got here, provider didn’t land *yet*.
            // Only delay anon bootstrap when a redirect was actually pending.
            if (hadPendingRedirect) {
                logAuthDebug('app-redirect-awaiting-late-provider', {
                    hadPendingRedirect,
                });
                clearTimeoutRedirectChecked();
                timeoutRedirectChecked();
            } else {
                logAuthDebug('app-redirect-no-pending-redirect');
                clearRedirectFlag();
                setRedirectStateChecked(true);
            }
        })();
    }, [redirectReady, hadPendingRedirect]);

    /**
     * Manages user session initialization
     */
    const currentAuthUserId = auth.currentUser?.uid || null;
    const userMatchesCurrentAuthUser = !!(
        user
        && currentAuthUserId != null
        && user.id === currentAuthUserId
    );
    useEffect(() => {
        if (
            user
            && userMatchesCurrentAuthUser
            && (
                !currentSessionId
                || (
                    currentSessionId
                    && browserTabIsFocused != null
                    && currentSessionLastActivityStartTimestamp != null
                    && browserTabIsFocused > currentSessionLastActivityStartTimestamp + NEW_SESSION_TIMEOUT_DURATION
                )
            )
            && !startingUserSession
        ) {
            setStartingUserSession(true);
            startSession();
        } else if (
            user
            && userMatchesCurrentAuthUser
            && currentSessionId != null
            && browserTabIsFocused != null
            && !startingUserSession
        ) {
            // update current session timestamp value
            setCurrentSessionLastActivityStartTimestamp(browserTabIsFocused);
        }
    }, [
        user,
        userMatchesCurrentAuthUser,
        currentSessionId,
        startingUserSession,
        browserTabIsFocused,
    ]);

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const utmSource = searchParams.get('utm_source');
        const normalizedUtmSource = utmSource != null
            ? normalizeTrafficSource(utmSource)
            : null;
        const referrer = document.referrer || '';
        const inferredReferrerSource = inferTrafficSourceFromReferrer(referrer);

        const source = normalizedUtmSource
            || inferredReferrerSource
            || TRAFFIC_SOURCE.unknown;

        initialTrafficSourcePayloadRef.current = {
            source,
            utmSource,
            referrer: referrer || null,
            inferred: normalizedUtmSource == null,
        };

        if (utmSource != null) {
            searchParams.delete('utm_source');
            const updatedSearch = searchParams.toString();
            const nextUrl = `${window.location.pathname}${updatedSearch ? `?${updatedSearch}` : ''}${window.location.hash}`;
            window.history.replaceState({}, document.title, nextUrl);
        }
    }, []);

    useEffect(() => {
        if (!currentSessionId) return;

        const sourceSessionStorageKey = `vs-source-recorded-${currentSessionId}`;
        try {
            if (sessionStorage.getItem(sourceSessionStorageKey) === '1') return;
        } catch {
            // noop
        }

        const payload = initialTrafficSourcePayloadRef.current;
        if (!payload) return;

        recordUserAction({
            test: isTestMode(),
            type: USER_ACTION_TYPE.captureTrafficSource,
            sessionId: currentSessionId,
            payload,
        });
        try {
            sessionStorage.setItem(sourceSessionStorageKey, '1');
        } catch {
            // noop
        }
    }, [currentSessionId]);

    /**
     * Subscribes to and listens for Firebase user authentication updates
     */
    const authUserLatest = useLatest({
        user,
        userCartItems,
        redirectStateChecked,
        signingInOut,
        authInitialized,
        signedIn,
    });
    const syncUserEmailVerified = useCallback((authUser: User | null): void => {
        setUserEmailVerified(!!authUser && !authUser.isAnonymous && !!authUser.emailVerified);
    }, []);
    const resolveHydratedAuthenticationType = useCallback((
        authUser: User,
        userItem: IPartialUserItem,
    ): AUTHENTICATION_TYPE => {
        if (
            authUser.isAnonymous
            || userItem.authenticationType !== AUTHENTICATION_TYPE.anonymous
        ) {
            return userItem.authenticationType;
        }

        const providerId = authUser.providerData?.[0]?.providerId;
        switch (providerId) {
        case FIREBASE_AUTH_SIGN_IN_PROVIDER_ID_TYPE.google:
            return AUTHENTICATION_TYPE.google;
        case FIREBASE_AUTH_SIGN_IN_PROVIDER_ID_TYPE.github:
            return AUTHENTICATION_TYPE.github;
        case FIREBASE_AUTH_SIGN_IN_PROVIDER_ID_TYPE.facebook:
            return AUTHENTICATION_TYPE.facebook;
        case FIREBASE_AUTH_SIGN_IN_PROVIDER_ID_TYPE.xTwitter:
            return AUTHENTICATION_TYPE.xTwitter;
        case FIREBASE_AUTH_SIGN_IN_PROVIDER_ID_TYPE.email:
            return AUTHENTICATION_TYPE.email;
        default:
            // Stale callable cache can briefly return anonymous user docs
            // after auth upgrades; prefer authenticated fallback for UI.
            return authUser.email
                ? AUTHENTICATION_TYPE.email
                : userItem.authenticationType;
        }
    }, []);
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (authUser) => {
            syncUserEmailVerified(authUser);
            const authUserCurrent = authUserLatest.current;
            const isSignOutToAnonymousTransition = (
                signOutToAnonymousStartedAtRef.current != null
            );

            // Redirect provider sign-in can briefly emit anonymous/null during reload.
            // Ignore those transient events so UI does not regress to guest for ~seconds.
            // Still allow authenticated users through immediately (no delayed provider UI).
            if (!authUserCurrent.redirectStateChecked && (!authUser || authUser.isAnonymous)) return;

            // Allow redirect-authenticated users through even if signingInOut
            // hasn't been flipped yet by the dialog restore path.
            if (
                authUserCurrent.signingInOut
                && (!authUser || authUser.isAnonymous)
                && !isSignOutToAnonymousTransition
            ) return;
            if (
                authUserCurrent.signingInOut
                && isSignOutToAnonymousTransition
                && (
                    !authUser
                    || authUser.isAnonymous
                )
            ) {
                // Successful sign-out-to-anonymous now releases the shared busy
                // flag from the auth listener, not from forceSignOutToAnonymous().
                // That keeps ReaderView pinned in its placeholder handoff until
                // Firebase has actually left the previous authenticated session.
                setSigningInOut(false);
            }
            // Sign-out-to-anonymous can resubscribe and immediately replay the old
            // authenticated Firebase user before sign-out actually clears auth.
            // Keep the shared busy flag pinned through that replay so reader
            // placeholders do not briefly drop back to stale gated content.
            if (
                authUserCurrent.signingInOut
                && authUser
                && !authUser.isAnonymous
                && !isSignOutToAnonymousTransition
            ) {
                setSigningInOut(false);
            }

            if (!authUserCurrent.authInitialized) {
                setAuthInitialized(true);
            }

            const shouldHydrateAuthenticatedUser = !!(
                authUser
                && !authUser.isAnonymous
                && (
                    !authUserCurrent.signedIn
                    || authUserCurrent.signedIn === AUTHENTICATION_TYPE.anonymous
                    || !authUserCurrent.user
                    || authUserCurrent.user.id !== authUser.uid
                    || authUserCurrent.user.authenticationType === AUTHENTICATION_TYPE.anonymous
                )
            );
            if (shouldHydrateAuthenticatedUser && authUser) {
                // User is signed in, see docs for a list of available properties
                // https://firebase.google.com/docs/reference/js/firebase.User
                //
                const { uid } = authUser;
                refreshUserDataNow(uid).then((userItem) => {
                    if (userItem) {
                        const hydratedAuthenticationType = resolveHydratedAuthenticationType(
                            authUser,
                            userItem,
                        );
                        setUser({
                            ...userItem,
                            authenticationType: hydratedAuthenticationType,
                        });
                        syncUserEmailVerifiedFromUserItem({
                            ...userItem,
                            authenticationType: hydratedAuthenticationType,
                        });
                        setSignedIn(hydratedAuthenticationType);
                        logUserData({ userItem });
                    }
                });
            } else if (authUser && authUser.isAnonymous) {
                if (
                    signOutToAnonymousStartedAtRef.current != null
                    && !signOutToAnonymousAuthReadyLoggedRef.current
                ) {
                    signOutToAnonymousAuthReadyLoggedRef.current = true;
                    logAuthDebug('signout-to-anonymous-auth-ready', {
                        elapsedMs: Date.now() - signOutToAnonymousStartedAtRef.current,
                    });
                }
                clearTimeoutCreateAnonymousUserDoc();
                timeoutCreateAnonymousUserDoc(authUser);
            }
        });

        return function cleanup() {
            if (unsubscribe) unsubscribe();
        };
    }, [
        signingInOut,
        signedIn,
        redirectStateChecked,
        hadPendingRedirect,
        syncUserEmailVerified,
        syncUserEmailVerifiedFromUserItem,
        resolveHydratedAuthenticationType,
    ]);

    const authHydrationLatest = useLatest({
        signedIn,
        userId: user?.id,
        userAuthenticationType: user?.authenticationType,
    });
    useEffect(() => onIdTokenChanged(auth, (authUser) => {
        syncUserEmailVerified(authUser);
        const hydrationCurrent = authHydrationLatest.current;

        const shouldHydrateFromToken = !!(
            authUser
            && !authUser.isAnonymous
            && (
                !hydrationCurrent.signedIn
                || hydrationCurrent.signedIn === AUTHENTICATION_TYPE.anonymous
                || !hydrationCurrent.userId
                || hydrationCurrent.userId !== authUser.uid
                || hydrationCurrent.userAuthenticationType === AUTHENTICATION_TYPE.anonymous
            )
        );

        if (shouldHydrateFromToken) {
            refreshUserDataNow(authUser!.uid)
                .then((userItem) => {
                    if (!userItem) return;
                    const hydratedAuthenticationType = resolveHydratedAuthenticationType(
                        authUser!,
                        userItem,
                    );
                    setUser({
                        ...userItem,
                        authenticationType: hydratedAuthenticationType,
                    });
                    syncUserEmailVerifiedFromUserItem({
                        ...userItem,
                        authenticationType: hydratedAuthenticationType,
                    });
                    setSignedIn(hydratedAuthenticationType);
                    logUserData({ userItem });
                })
                .catch(() => null);
        }
    // Keep this token listener stable; live auth state is read from useLatest inside the callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [
        authHydrationLatest,
        syncUserEmailVerified,
        syncUserEmailVerifiedFromUserItem,
        resolveHydratedAuthenticationType,
    ]);

    useEffect(() => {
        const redirecting = sessionStorage.getItem('vs_redirecting') === '1';
        if (
            redirectStateChecked
            && !redirecting
            && !signingInOut
            && !signedIn
            && !auth.currentUser
        ) {
            // Conditional avoids bug where authentication listener creates an anonymous account
            // between signing up using email and rerendering.
            clearTimeoutSignInAnonymously();
            timeoutSignInAnonymously();
        }
    }, [
        auth,
        signedIn,
        signingInOut,
        redirectStateChecked,
    ]);

    /**
     * Listens for changes to user database object
     * and updates local object copy
     */

    // keep all changing values fresh for the snapshot callback
    const latest = useLatest({
        user,
        signingInOut,
        lastUserPingVersion,
        subscribedToUserChanges,
        fetchUserData,
        fetchUserPostAccess,
        fetchUserCartItems,
        setSubscribedToUserChanges,
        setLastUserPingVersion,
    });
    const unsubscribeRef = React.useRef<Unsubscribe | null>(null);
    const previousSigningInOutRefreshRef = useRef<boolean>(signingInOut);
    // (Re)subscribe when the *conditions* change; do manual teardown inside.
    useEffect(() => {
        const cur = latest.current;

        // If we shouldn't be listening now, tear down and bail.
        if (!cur.user || cur.signingInOut) {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
            return;
        }

        // If already subscribed, reset before creating a new one.
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
        }

        const db = getFirestore();
        const usersCollection = isTestMode()
            ? FIRESTORE_COLLECTION.stagingUsers
            : FIRESTORE_COLLECTION.users;

        const pingRef = doc(db, `${usersCollection}/${cur.user.id}/ping/current`);

        unsubscribeRef.current = onSnapshot(
            pingRef,
            (pingSnapshot) => {
                const snapshotCur = latest.current;

                if (!pingSnapshot.exists()) {
                    // no user update yet
                    return;
                }

                // user but no ping item yet (will be made when user is next updated)
                const { version } = pingSnapshot.data() as { version: number };

                const snapshotUser = snapshotCur.user;
                if (snapshotUser == null) return;
                if (!shouldProcessUserPingVersion({
                    signingInOut: snapshotCur.signingInOut,
                    hasUser: true,
                    version,
                    lastUserPingVersion: snapshotCur.lastUserPingVersion,
                })) {
                    return;
                }
                snapshotCur.setLastUserPingVersion(version);
                snapshotCur.fetchUserData(snapshotUser.id, true);
                snapshotCur.fetchUserPostAccess(snapshotUser.id, true);
                snapshotCur.fetchUserCartItems(snapshotUser.id, true);

                if (!snapshotCur.subscribedToUserChanges) snapshotCur.setSubscribedToUserChanges(true);
            },
            (error: FirestoreError) => {
                const snapshotCur = latest.current;
                if (shouldIgnoreTransientUserPingPermissionDenied({
                    errorCode: error.code,
                    signingInOut: snapshotCur.signingInOut,
                    authUserId: auth.currentUser?.uid || null,
                    localUserId: snapshotCur.user?.id || null,
                })) {
                    if (shouldShowPreviewDebugLogs && typeof window !== 'undefined') {
                        // eslint-disable-next-line no-console
                        console.warn('[auth] ignoring transient ping snapshot permission-denied', {
                            path: pingRef.path,
                            authUserId: auth.currentUser?.uid || null,
                            localUserId: snapshotCur.user?.id || null,
                            signingInOut: snapshotCur.signingInOut,
                        });
                    }
                    if (unsubscribeRef.current) {
                        unsubscribeRef.current();
                        unsubscribeRef.current = null;
                    }
                    return;
                }

                if (currentSessionId) {
                    recordUserAction({
                        test: isTestMode(),
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            error: `User ping snapshot failed: ${error.code}: ${error.message}`,
                            source: 'app-user-ping-snapshot',
                            path: pingRef.path,
                        },
                    });
                }
            },
        );
    }, [
        // no return cleanup here
        user?.id,
        signingInOut,
    ]);
    // Unmount-only cleanup
    useEffect(() => () => {
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
        }
    }, []);

    useEffect(() => {
        const wasSigningInOut = previousSigningInOutRefreshRef.current;
        previousSigningInOutRefreshRef.current = signingInOut;
        const currentAuthUser = auth.currentUser;
        if (!shouldRefreshUserDerivedCachesAfterAuthHandoff({
            wasSigningInOut,
            signingInOut,
            authUser: currentAuthUser,
        })) return;
        if (!currentAuthUser) return;

        fetchUserData(currentAuthUser.uid, true);
        fetchUserPostAccess(currentAuthUser.uid, true);
        fetchUserCartItems(currentAuthUser.uid, true);
        if (!subscribedToUserChanges) setSubscribedToUserChanges(true);
    }, [
        signingInOut,
        subscribedToUserChanges,
        fetchUserData,
        fetchUserPostAccess,
        fetchUserCartItems,
    ]);

    /**
     * Fetches and saves user email
     */
    useEffect(() => {
        if (
            user != null
            && user.authenticationType !== AUTHENTICATION_TYPE.anonymous
            && !fetchingUserEmail
        ) {
            fetchUserEmail(user.id);
        } else if (
            (
                user == null
                || user.authenticationType === AUTHENTICATION_TYPE.anonymous
            )
            && userEmail != null
        ) {
            setUserEmail(null);
        }
    }, [
        user?.id,
        user?.authenticationType,
    ]);

    /**
     * Reset admin-status fetch state when the identity/auth mode changes.
     * Anonymous users intentionally short-circuit this path, so an anon->auth
     * upgrade on the same UID must clear the fetched flag and refetch.
     */
    useEffect(() => {
        const previous = lastAdminStatusSubjectRef.current;
        const nextUserId = user?.id || null;
        const nextAuthenticationType = user?.authenticationType || null;
        const subjectChanged = didUserHydrationSubjectChange(previous, {
            userId: nextUserId,
            authenticationType: nextAuthenticationType,
        });

        if (
            subjectChanged
            && user != null
            && user.authenticationType !== AUTHENTICATION_TYPE.anonymous
            && fetchedUserAdminStatus
        ) {
            setFetchedUserAdminStatus(false);
        }

        lastAdminStatusSubjectRef.current = {
            userId: nextUserId,
            authenticationType: nextAuthenticationType,
        };
    }, [
        user?.id,
        user?.authenticationType,
        fetchedUserAdminStatus,
    ]);

    /**
     * Fetches and saves user admin status
     */
    useEffect(() => {
        if (
            user != null
            && user.authenticationType !== AUTHENTICATION_TYPE.anonymous
            && !fetchedUserAdminStatus
            && !fetchingUserAdminStatus
        ) {
            fetchUserAdminStatus(user.id);
        } else if (user == null) {
            if (userIsAdmin) setUserIsAdmin(false);
            if (fetchedUserAdminStatus) setFetchedUserAdminStatus(false);
        } else if (
            user != null
            && user.authenticationType === AUTHENTICATION_TYPE.anonymous
        ) {
            if (userIsAdmin) setUserIsAdmin(false);
            setFetchedUserAdminStatus(true);
        }
    }, [
        user?.id,
        user?.authenticationType,
        fetchedUserAdminStatus,
        fetchingUserAdminStatus,
    ]);

    /**
     * Fetches and saves user post access
     */
    useEffect(() => {
        if (
            user != null
            && user.authenticationType !== AUTHENTICATION_TYPE.anonymous
            && !fetchingUserPostAccess
        ) {
            fetchUserPostAccess(user.id);
        } else if (
            (
                user == null
                || user.authenticationType === AUTHENTICATION_TYPE.anonymous
            )
            && userPostAccess != null
        ) {
            setUserPostAccess(undefined);
        }
    }, [
        user?.id,
        user?.authenticationType,
    ]);

    /**
     * Fetches and saves cart items
     */
    useEffect(() => {
        if (
            user != null
            && !fetchingUserCartItems
        ) {
            fetchUserCartItems(user.id);
        } else if (
            user == null
            && userCartItems != null
        ) {
            setUserCartItems(undefined);
        }
    }, [
        user?.id,
        user?.authenticationType,
    ]);

    /**
     * Manages which cursor action icon should be present at any
     * given moment
     */
    useInterval(() => {
        const index = (cursorSignIndex + 1) % cursorSigns.length;
        setCursorSignIndex(index);
    }, cursorSigns.length > 1 ? CURSOR_SIGN_ALTERNATE_DURATION : null);

    /**
     * Manages event listener that tracks hidden cursor movement
     */
    useEffect(() => {
        // Modify cursor position
        document.body.addEventListener('mousemove', modifyCursorPosition);

        return () => {
            document.body.removeEventListener('mousemove', modifyCursorPosition);
            if (cursorPositionAnimationFrameRef.current != null) {
                window.cancelAnimationFrame(cursorPositionAnimationFrameRef.current);
                cursorPositionAnimationFrameRef.current = null;
            }
        };
    }, [
        cursorLockedTargetProperties,
        cursorTarget,
        cursorType,
        temporarilyUnlockCursor,
    ]);

    /**
     * Manages unlocking cursor when small cursor is engaged
     * and a user interface object is grabbed.
     * Necessary for custom cursor to move freely along with grabbed object
     */
    useEffect(() => {
        if (
            customCursorActive
            && checkUnlockCursor
            && cursorLockedTargetProperties
            && cursorTarget
            && cursorType === CURSOR_TYPE.engagedSmall
            && !temporarilyUnlockCursor
        ) {
            // Encountered when grabbing an interactable object with the small cursor

            // Note that ref.current may be null. This is expected, because you may
            // conditionally render the ref-ed element, or you may forgot to assign it
            if (!customCursorRef.current) return;

            setTemporarilyUnlockCursor(true);

            customCursorRef.current.style.borderColor = theme.color.white;
            customCursorRef.current.style.borderWidth = `${CURSOR_STANDARD_BORDER_WIDTH}px`;
            customCursorRef.current.style.backgroundColor = theme.color.white;
            customCursorRef.current.style.width = `${10}px`;
            customCursorRef.current.style.height = `${10}px`;
            const target = findParentNodeWithClass(HOVER_TARGET_CLASSNAME, cursorTarget, 5);
            if (!target) return;
            const targetRect = target.getBoundingClientRect();
            const cursorX = targetRect.right - targetRect.width / 2;
            const cursorY = targetRect.bottom - targetRect.height / 2;
            customCursorRef.current.style.transform = `translate(calc(${cursorX}px - 50%), calc(${cursorY}px - 50%))`;
        } else if (
            customCursorActive
            && checkUnlockCursor
            && cursorLockedTargetProperties
            && cursorTarget
            && temporarilyUnlockCursor
        ) {
            // Encountered when dropping a grabbed interactable object with the small cursor
            setTemporarilyUnlockCursor(false);
            // Note that ref.current may be null. This is expected, because you may
            // conditionally render the ref-ed element, or you may forgot to assign it
            if (!customCursorRef.current) return;

            customCursorRef.current.style.borderColor = theme.color.white;
            customCursorRef.current.style.borderWidth = `${CURSOR_EXPAND_BORDER_WIDTH}px`;
            customCursorRef.current.style.backgroundColor = '';
            customCursorRef.current.style.width = `${cursorLockedTargetProperties.width + 2 * CURSOR_LOCKED_PADDING}px`;
            customCursorRef.current.style.height = `${cursorLockedTargetProperties.height + 2 * CURSOR_LOCKED_PADDING}px`;

            // Update Cursor Lock Position
            const target = findParentNodeWithClass(HOVER_TARGET_CLASSNAME, cursorTarget, 5);
            if (!target) return;
            const targetRect = target.getBoundingClientRect();

            setCursorLockedTargetProperties({
                width: cursorLockedTargetProperties.width,
                height: cursorLockedTargetProperties.height,
                pos: {
                    x: targetRect.right - targetRect.width / 2 - cursorLockedTargetProperties.width / 2,
                    y: targetRect.bottom - targetRect.height / 2 - cursorLockedTargetProperties.height / 2,
                },
            });
        }

        setCheckUnlockCursor(false);
    }, [
        checkUnlockCursor,
    ]);

    /**
     * Manages toggling custom cursor activity based on device, performance,
     * and the user's saved preference.
     */
    useEffect(() => {
        setCustomCursorActive(resolveCustomCursorActive({
            customCursorEnabled: user?.customCursorEnabled,
            isMobileDevice: isMobile(),
            performanceGateOff: off,
        }));
    }, [
        off,
        user?.customCursorEnabled,
    ]);

    /**
     * Prevents jarring cursor movement when traveling over element
     * without intent of hovering over it
     */
    const {
        start: timeoutCursorSmallTransform,
        clear: clearTimeoutCursorSmallTransform,
    } = useTimeout((props) => {
        const args = props as {
            cursor: HTMLDivElement,
            rect: DOMRect,
            target: HTMLElement,
        };
        transformToSmallCursor(args.cursor, args.target, args.rect);
        setCursorType(CURSOR_TYPE.engagedSmall);
        setTransitioningToSmallCursor(false);
    }, SMALL_CURSOR_TRANSFORMATION_DELAY_DURATION);

    /**
     * Determines which corner cursor actions should be placed
     * based on position of the custom cursor
     */
    const {
        start: timeoutComputeSmallCursorCorner,
        clear: clearTimeoutComputeSmallCursorCorner,
    } = useTimeout(() => {
        if (customCursorRef.current) {
            const cursorRect = customCursorRef.current.getBoundingClientRect();
            const cursorActionCornerPosition: ICursorActionCornerPosition = {
                top: true,
                left: false,
            };

            if (cursorRect.top - SMALL_CURSOR_ACTION_CONTAINER_LENGTH < 0) {
                cursorActionCornerPosition.top = false;
            }

            if (cursorRect.right + SMALL_CURSOR_ACTION_CONTAINER_LENGTH > viewportDimensions.width) {
                cursorActionCornerPosition.left = true;
            }

            setSmallCursorCorner(cursorActionCornerPosition);
        }
    }, SMALL_CURSOR_COMPUTER_CORNER_DELAY_DURATION);

    /**
     * Manages changing state to indicate that we've checked for redirect auth state
     */
    const {
        start: timeoutRedirectChecked,
        clear: clearTimeoutRedirectChecked,
    } = useTimeout(() => {
        // Only now, if STILL no user and STILL not signed in,
        // allow the anon bootstrap logic elsewhere to proceed.
        clearRedirectFlag(); // treat it as failed redirect
        setRedirectStateChecked(true);
        if (hadPendingRedirect) {
            setSnackbarData({
                visible: true,
                duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_REDIRECT_SIGN_IN_ERROR,
                icon: CautionIcon,
                hasFailure: true,
                dismissable: true,
            });
        }
    }, AUTH_REDIRECT_TIMEOUT_DURATION);

    /**
     * Manages signing in a user anonymously
     */
    const {
        start: timeoutSignInAnonymously,
        clear: clearTimeoutSignInAnonymously,
    } = useTimeout(() => {
        // re-check in case user appeared in the meantime
        if (!auth.currentUser) {
            // User is not signed in
            // Create anonymous user
            signInAnonymously(auth).then(() => {
                anonymousSignInRetryAttemptRef.current = 0;
                if (anonymousSignInRetryTimeoutRef.current != null) {
                    clearTimeout(anonymousSignInRetryTimeoutRef.current);
                    anonymousSignInRetryTimeoutRef.current = null;
                }
            }).catch(() => {
                const retryAttempt = anonymousSignInRetryAttemptRef.current;
                const hasRetryRemaining = retryAttempt < ANONYMOUS_SIGN_IN_RETRY_DELAYS_MS.length;
                if (hasRetryRemaining) {
                    const retryDelay = ANONYMOUS_SIGN_IN_RETRY_DELAYS_MS[retryAttempt];
                    anonymousSignInRetryAttemptRef.current += 1;
                    if (anonymousSignInRetryTimeoutRef.current == null) {
                        anonymousSignInRetryTimeoutRef.current = window.setTimeout(() => {
                            anonymousSignInRetryTimeoutRef.current = null;
                            clearTimeoutSignInAnonymously();
                            timeoutSignInAnonymously();
                        }, retryDelay);
                    }
                    return;
                }

                anonymousSignInRetryAttemptRef.current = 0;
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_ANONYMOUS_SIGN_IN_ERROR,
                    icon: CautionIcon,
                    hasFailure: true,
                });
            });
            // We set flag to false to make way for a new user
            if (subscribedToUserChanges) setSubscribedToUserChanges(false);
            if (currentSessionId) {
                // We likely signed out
                // Indicate sign out timestamp
                // Clear current session id to make way for new session
                setCurrentSessionId(null);
            }
        }
    }, ANONYMOUS_SIGN_IN_TIMEOUT_DURATION);

    const forceSignOutToAnonymous = useCallback(async (): Promise<boolean> => {
        let success = false;
        if (
            signOutToAnonymousStartedAtRef.current == null
            && signedIn != null
            && signedIn !== AUTHENTICATION_TYPE.anonymous
        ) {
            signOutToAnonymousStartedAtRef.current = Date.now();
            signOutToAnonymousAuthReadyLoggedRef.current = false;
            signOutToAnonymousUserReadyLoggedRef.current = false;
            logAuthDebug('signout-to-anonymous-started', {
                authenticationType: signedIn,
                source: 'force-signout',
            });
        }
        try {
            await signOut(auth);
            // Communicates to parent that we are not authenticated
            setSignedIn(null);
            // Clear user so it is not used to repopulate new anonymous user item
            setUser(null);
            // Sign-out should not retain reader fallback history from the previous account.
            try {
                window.localStorage.removeItem(LOCAL_READER_PROGRESS_STORAGE_KEY);
            } catch (error) {
                // best effort cleanup only
            }
            // Clear current session id to make way for new session
            // We put setCurrentSessionLastActivityStartTimestamp first because
            // it doesn't trigger any side effects
            // But setCurrentSessionId does, which may assume a most recent
            // value for currentSessionTimestamp
            setCurrentSessionLastActivityStartTimestamp(null);
            setCurrentSessionId(null);
            // Fast-path anonymous auth bootstrap; App-level fallback remains active.
            signInAnonymously(auth).catch(() => null);
            success = true;
        } catch {
            success = false;
        } finally {
            if (!success) {
                // Failed sign-out should release the shared auth busy lock
                // immediately because no auth-state downgrade will follow.
                setSigningInOut((current) => (current ? false : current));
            }
        }
        return success;
    }, [signedIn, LOCAL_READER_PROGRESS_STORAGE_KEY]);

    useEffect(() => (
        () => {
            if (anonymousSignInRetryTimeoutRef.current != null) {
                clearTimeout(anonymousSignInRetryTimeoutRef.current);
                anonymousSignInRetryTimeoutRef.current = null;
            }
        }
    ), []);

    /**
     * Manages creating a Firestore doc for new anonymous user
     */
    const {
        start: timeoutCreateAnonymousUserDoc,
        clear: clearTimeoutCreateAnonymousUserDoc,
    } = useTimeout((authUser) => {
        const { uid } = authUser;
        if (creatingAnonymousUserDocForUidRef.current === uid) return;
        creatingAnonymousUserDocForUidRef.current = uid;
        const anonymousProvisionStartedAt = Date.now();
        const logAnonymousProvisionTiming = (
            event: string,
            data?: Record<string, unknown>,
        ): void => {
            logAuthDebug(event, {
                uid,
                elapsedMs: Date.now() - anonymousProvisionStartedAt,
                duringSignOutTransition: signOutToAnonymousStartedAtRef.current != null,
                ...data,
            });
        };
        const finishAnonymousUserProvision = (
            anonymousUserItemToSet: IPartialUserItem,
            path: string,
            source: string,
        ): void => {
            if (
                signOutToAnonymousStartedAtRef.current != null
                && !signOutToAnonymousUserReadyLoggedRef.current
            ) {
                signOutToAnonymousUserReadyLoggedRef.current = true;
                logAuthDebug('signout-to-anonymous-user-ready', {
                    elapsedMs: Date.now() - signOutToAnonymousStartedAtRef.current,
                    source,
                });
                signOutToAnonymousStartedAtRef.current = null;
            }
            setUser({
                ...anonymousUserItemToSet,
            });
            logAnonymousProvisionTiming('anonymous-provision-user-ready', {
                path,
            });
            logUserData({ userItem: anonymousUserItemToSet });
        };
        logAnonymousProvisionTiming('anonymous-provision-create-started');
        const providerUser = auth.currentUser?.providerData[0];
        let firstName = DEFAULT_USER_FIRST_NAME;
        let lastName = new ShortUniqueId({ length: 6 })(); // avoid anonymous users with same names
        let phoneNumber: string | undefined;
        let photoURL: string | undefined;
        if (
            providerUser
            && providerUser.displayName
        ) {
            const displayNameParts = providerUser.displayName.split(' ');
            [firstName] = displayNameParts;
            lastName = displayNameParts.length > 1
                ? displayNameParts[1]
                : undefined;
        }
        if (
            providerUser
            && providerUser.phoneNumber
        ) {
            ({ phoneNumber } = providerUser);
        }
        if (
            providerUser
            && providerUser.photoURL
        ) {
            ({ photoURL } = providerUser);
        }

        // Create-first provisioning avoids an extra getUser round-trip on sign-out
        // while keeping idempotent behavior for existing anonymous users.
        createAnonymousUser({
            test: isTestMode(),
            firstName,
            lastName,
            phoneNumber,
            photoURL,
        }).then((createdAnonymousUserItem) => {
            logAnonymousProvisionTiming('anonymous-provision-create-succeeded');
            return createdAnonymousUserItem;
        }).catch(async (error) => {
            logAnonymousProvisionTiming('anonymous-provision-create-failed', {
                error: (error as { message?: string } | undefined)?.message || String(error),
            });
            // Race-safe fallback: if user doc exists despite callable error shape,
            // treat as success and continue with the existing anonymous user.
            const existingUserItem = await refreshUserDataNow(uid).catch(() => null);
            if (!existingUserItem) throw error;
            logAnonymousProvisionTiming('anonymous-provision-fallback-read-succeeded');
            return existingUserItem;
        }).then(async (anonymousUserItem) => {
            let resolvedAnonymousUserItem = anonymousUserItem;
            if (signOutToAnonymousStartedAtRef.current != null) {
                for (let i = 0; i < ANONYMOUS_USER_DOC_CONFIRM_RETRY_DELAYS_MS.length; i += 1) {
                    if (ANONYMOUS_USER_DOC_CONFIRM_RETRY_DELAYS_MS[i] > 0) {
                        // eslint-disable-next-line no-await-in-loop
                        await waitForMilliseconds(ANONYMOUS_USER_DOC_CONFIRM_RETRY_DELAYS_MS[i]);
                    }
                    // eslint-disable-next-line no-await-in-loop
                    const confirmedAnonymousUserItem = await refreshUserDataNow(uid).catch(() => null);
                    if (confirmedAnonymousUserItem) {
                        resolvedAnonymousUserItem = confirmedAnonymousUserItem;
                        logAnonymousProvisionTiming('anonymous-provision-confirm-read-succeeded', {
                            attempt: i + 1,
                        });
                        break;
                    }
                }
            }
            const cur = authUserLatest.current;
            const shouldMigrateFromPreviousAnonymousUser = !!(
                cur.user
                && cur.user.authenticationType === AUTHENTICATION_TYPE.anonymous
            );
            if (shouldMigrateFromPreviousAnonymousUser && cur.user) {
                // Method only migrates non-sensitive partial user item data
                // Old user data should only be anonymous user items which
                // should not be able to accrue meaningful information outside of
                // user sessions and update timestamps (for name changes)
                let oldUserEmail: string | undefined;
                let oldUserSessions: string[] = [];
                let oldUserUpdateTimestamps: number[] = [];
                try {
                    const existingUserEmailSessionsAndUpdateTimestamps = await getUserEmailSessionItemsAndUpdateTimestamps({
                        test: isTestMode(),
                    });
                    oldUserEmail = existingUserEmailSessionsAndUpdateTimestamps.email || undefined;
                    oldUserSessions = existingUserEmailSessionsAndUpdateTimestamps.sessions;
                    oldUserUpdateTimestamps = existingUserEmailSessionsAndUpdateTimestamps.updated;
                } catch (error) {
                    if (currentSessionId) {
                        recordUserAction({
                            test: isTestMode(),
                            type: USER_ACTION_TYPE.error,
                            sessionId: currentSessionId,
                            payload: {
                                error: `Failed to fetch optional email-session context during anonymous auth transition: ${(error as Error)?.message || String(error)}`,
                                source: 'app-anonymous-auth-transition-context-fetch',
                            },
                        });
                    }
                }
                const migratedUser = await migrateOldUserDataToNewItem({
                    test: isTestMode(),
                    oldUser: cur.user,
                    oldUserEmail: oldUserEmail || undefined,
                    oldUserCart: cur.userCartItems || [],
                    oldUserSessions,
                    oldUserUpdateTimestamps,
                });
                if (!migratedUser) {
                    if (currentSessionId) {
                        recordUserAction({
                            test: isTestMode(),
                            type: USER_ACTION_TYPE.error,
                            sessionId: currentSessionId,
                            payload: {
                                error: `There was a problem authenticating your account. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                            },
                        });
                    }
                    throw Error(`There was a problem authenticating your account. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
                }
                // Set immediately for faster UI readiness, then reconcile from source of truth.
                setUser({
                    ...migratedUser,
                });
                logAnonymousProvisionTiming('anonymous-provision-user-ready', {
                    path: 'migrated-old-user',
                });
                logUserData({ userItem: migratedUser });
                return;
            }

            if (resolvedAnonymousUserItem.mailingListSubscription == null) {
                // User doesn't have a mailing list attribute
                // Give it to them
                const mailingListSubscriptionValue: IMailingListSubscriptionValue = {
                    subscribed: false,
                    timestamp: Date.now(),
                };
                const resolvedAnonymousUserItemWithMailingList: IPartialUserItem = {
                    ...resolvedAnonymousUserItem,
                    mailingListSubscription: {
                        history: [mailingListSubscriptionValue],
                    },
                };
                const canBackfillMailingListRemotely = (
                    auth.currentUser?.uid === resolvedAnonymousUserItem.id
                    && signOutToAnonymousStartedAtRef.current == null
                );
                if (!canBackfillMailingListRemotely) {
                    finishAnonymousUserProvision(
                        resolvedAnonymousUserItemWithMailingList,
                        'mailing-list-local-backfill',
                        'provisioned-anonymous-user-with-local-mailing-list-init',
                    );
                    return;
                }
                updateUser({
                    test: isTestMode(),
                    mailingListSubscriptionToAdd: [mailingListSubscriptionValue],
                }).then(() => {
                    finishAnonymousUserProvision(
                        resolvedAnonymousUserItemWithMailingList,
                        'mailing-list-backfill',
                        'provisioned-anonymous-user-with-mailing-list-init',
                    );
                }).catch((error) => {
                    logAnonymousProvisionTiming('anonymous-provision-mailing-list-backfill-failed', {
                        error: (error as { message?: string } | undefined)?.message || String(error),
                    });
                    finishAnonymousUserProvision(
                        resolvedAnonymousUserItemWithMailingList,
                        'mailing-list-local-backfill',
                        'provisioned-anonymous-user-with-local-mailing-list-init',
                    );
                });
                return;
            }

            finishAnonymousUserProvision(
                resolvedAnonymousUserItem,
                'provisioned-anonymous-user',
                'provisioned-anonymous-user',
            );
        })
            .finally(() => {
                if (creatingAnonymousUserDocForUidRef.current === uid) {
                    creatingAnonymousUserDocForUidRef.current = null;
                }
            });
    }, ANONYMOUS_CREATE_TIMEOUT_DURATION);

    /**
     * Manages event listener that tracks viewport resizing
     */
    useEventListener('resize', handleResize);

    /**
     * Prepares renderer for when an uncaught error is encountered
     */
    const fallbackRender = useCallback(({
        error,
        resetErrorBoundary,
    }: {
        error: any,
        resetErrorBoundary: any,
    }) => (
        <ErrorFallback
            error={error}
            customCursorActive={customCursorActive}
            currentSessionId={currentSessionId}
            user={user}
            resetErrorBoundary={resetErrorBoundary}
            onHoverEnter={(e) => onCursorEnter(
                CURSOR_TARGET.resetButton,
                [CURSOR_SIGN.click],
                e?.target as HTMLElement,
            )}
            onHoverLeave={onCursorLeave}
        />
    ), [
        user,
        currentSessionId,
        customCursorActive,
    ]);

    // ===== Memoization =====

    const hasUser = authInitialized && !!auth.currentUser;
    const hasAuthenticatedAuthUser = !!(
        redirectStateChecked
        && auth.currentUser
        && !auth.currentUser.isAnonymous
    );
    const isRecognizedUser = !!(
        user
            ? user.authenticationType !== AUTHENTICATION_TYPE.anonymous
            : hasAuthenticatedAuthUser
    );
    /**
     * Memoizes a renderer for the custom cursor
     */
    const CustomCursorSigns = useMemo(() => (
        <>
            <CustomCursorSign
                isVisible={cursorSigns.length === 1 && cursorType !== CURSOR_TYPE.resting}
                small={
                    cursorTargetType === CURSOR_TARGET.input
                    || cursorType === CURSOR_TYPE.engagedSmall
                    || cursorType === CURSOR_TYPE.line
                }
                transitionDuration={CURSOR_TRANSITION_DURATION}
                transitionDelayDuration={SMALL_CURSOR_ACTION_DELAY_DURATION}
                src={inputFocused && cursorTargetType === CURSOR_TARGET.input
                    ? ChatSign
                    : cursorSigns[0]}
            />
            {cursorSigns.map((action: string, i: number) => (
                <CustomCursorSign
                    key={action}
                    isVisible={cursorSigns.length > 1 && cursorSignIndex === i && cursorType !== CURSOR_TYPE.resting}
                    small={
                        cursorTargetType === CURSOR_TARGET.input
                        || cursorType === CURSOR_TYPE.engagedSmall
                        || cursorType === CURSOR_TYPE.line
                    }
                    transitionDuration={CURSOR_TRANSITION_DURATION}
                    transitionDelayDuration={SMALL_CURSOR_ACTION_DELAY_DURATION}
                    src={action}
                />
            ))}
        </>
    ), [
        cursorSigns,
        inputFocused,
        cursorSignIndex,
        cursorTargetType,
        cursorType,
        temporarilyUnlockCursor,
    ]);

    // ===== Rendering =====

    const RouterRoutes = useMemo(() => (
        <Routes>
            <Route
                path="/"
                element={(
                    <LandingView
                        customCursorActive={customCursorActive}
                        browserTabIsFocused={browserTabIsFocused}
                        hasSound={hasSound}
                        viewportDimensions={viewportDimensions}
                        landingPageContentRevealed={landingPageContentRevealed}
                        cuneiformTabletRevealed={cuneiformTabletRevealed}
                        currentSessionId={currentSessionId}
                        user={user}
                        userEmail={userEmail}
                        setSnackbarData={setSnackbarData}
                        setLandingPageContentRevealed={setLandingPageContentRevealed}
                        setCuneiformTabletRevealed={setCuneiformTabletRevealed}
                        setInputFocused={setInputFocused}
                        setCursorSigns={setCursorSigns}
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                        onCursorMouseDown={onCursorMouseDown}
                        onCursorMouseUp={onCursorMouseUp}
                    />
                )}
            />
            <Route
                path={PAGE_ROUTE.about}
                element={(
                    <AboutView
                        customCursorActive={customCursorActive}
                        currentSessionId={currentSessionId}
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                    />
                )}
            />
            <Route
                path={PAGE_ROUTE.privacyPolicy}
                element={(
                    <PrivacyView
                        customCursorActive={customCursorActive}
                        currentSessionId={currentSessionId}
                        user={user}
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                    />
                )}
            />
            <Route
                path={PAGE_ROUTE.termsOfUse}
                element={(
                    <TermsView
                        customCursorActive={customCursorActive}
                        currentSessionId={currentSessionId}
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                    />
                )}
            />
            <Route
                path={PAGE_ROUTE.book}
                element={(
                    <ReaderView
                        write={process.env.NODE_ENV === 'development' && userIsAdmin}
                        customCursorActive={customCursorActive}
                        browserTabIsFocused={browserTabIsFocused}
                        user={user}
                        userEmailVerified={userEmailVerified}
                        userEmail={userEmail}
                        userPostAccess={userPostAccess}
                        userIsAdmin={userIsAdmin}
                        fetchedUserAdminStatus={fetchedUserAdminStatus}
                        userCartItems={userCartItems}
                        hasSound={hasSound}
                        currentSessionId={currentSessionId}
                        viewportDimensions={viewportDimensions}
                        notifyUserToSignUpAfterAnnotationCreationAttempt={notifyUserToSignUpAfterAnnotationCreationAttempt}
                        stickyPostBannerActive={stickyPostBannerActive}
                        signingInOut={signingInOut}
                        queueRevealBuyButtons={queueRevealBuyButtons}
                        setStickyPostBannerActive={setStickyPostBannerActive}
                        setInputFocused={setInputFocused}
                        setCursorSigns={setCursorSigns}
                        setNotifyUserToSignUpAfterAnnotationCreationAttempt={setNotifyUserToSignUpAfterAnnotationCreationAttempt}
                        setSnackbarData={setSnackbarData}
                        setSigningInOut={setSigningInOut}
                        setRefreshNotifications={setRefreshNotifications}
                        setIsUserProfileDialogExpanded={onUserProfileDialogIsExpanded}
                        setQueueRevealBuyButtons={setQueueRevealBuyButtons}
                        setUserCartItems={setUserCartItemsWithSyncGuard}
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                    />
                )}
            >
                <Route
                    path=":annotationId"
                    element={(
                        <ReaderView
                            write={process.env.NODE_ENV === 'development' && userIsAdmin}
                            customCursorActive={customCursorActive}
                            browserTabIsFocused={browserTabIsFocused}
                            user={user}
                            userEmailVerified={userEmailVerified}
                            userEmail={userEmail}
                            userPostAccess={userPostAccess}
                            userIsAdmin={userIsAdmin}
                            fetchedUserAdminStatus={fetchedUserAdminStatus}
                            userCartItems={userCartItems}
                            hasSound={hasSound}
                            currentSessionId={currentSessionId}
                            viewportDimensions={viewportDimensions}
                            notifyUserToSignUpAfterAnnotationCreationAttempt={notifyUserToSignUpAfterAnnotationCreationAttempt}
                            stickyPostBannerActive={stickyPostBannerActive}
                            signingInOut={signingInOut}
                            queueRevealBuyButtons={queueRevealBuyButtons}
                            setStickyPostBannerActive={setStickyPostBannerActive}
                            setInputFocused={setInputFocused}
                            setCursorSigns={setCursorSigns}
                            setNotifyUserToSignUpAfterAnnotationCreationAttempt={setNotifyUserToSignUpAfterAnnotationCreationAttempt}
                            setSnackbarData={setSnackbarData}
                            setSigningInOut={setSigningInOut}
                            setRefreshNotifications={setRefreshNotifications}
                            setIsUserProfileDialogExpanded={onUserProfileDialogIsExpanded}
                            setQueueRevealBuyButtons={setQueueRevealBuyButtons}
                            setUserCartItems={setUserCartItemsWithSyncGuard}
                            onCursorEnter={onCursorEnter}
                            onCursorLeave={onCursorLeave}
                        />
                    )}
                />
            </Route>
            <Route
                path={PAGE_ROUTE.physicalBook}
                element={(
                    <PhysicalBookView
                        customCursorActive={customCursorActive}
                        currentSessionId={currentSessionId}
                        user={user}
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                    />
                )}
            />
            <Route
                path={PAGE_ROUTE.characters}
                element={(
                    <CharactersView
                        customCursorActive={customCursorActive}
                        hasSound={hasSound}
                        currentSessionId={currentSessionId}
                        user={user}
                        viewportDimensions={viewportDimensions}
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                    />
                )}
            />
            <Route
                path={PAGE_ROUTE.treasureChest}
                element={(
                    <TreasureChestView
                        customCursorActive={customCursorActive}
                        hasSound={hasSound}
                        currentSessionId={currentSessionId}
                        user={user}
                        viewportDimensions={viewportDimensions}
                        setCursorSigns={setCursorSigns}
                        setSnackbarData={setSnackbarData}
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                    />
                )}
            >
                <Route
                    path=":cartridgeId"
                    element={(
                        <TreasureChestView
                            customCursorActive={customCursorActive}
                            hasSound={hasSound}
                            currentSessionId={currentSessionId}
                            user={user}
                            viewportDimensions={viewportDimensions}
                            setCursorSigns={setCursorSigns}
                            setSnackbarData={setSnackbarData}
                            onCursorEnter={onCursorEnter}
                            onCursorLeave={onCursorLeave}
                        />
                    )}
                />
            </Route>
            <Route
                path={PAGE_ROUTE.socialEmergence}
                element={(
                    <SocialEmergenceView
                        customCursorActive={customCursorActive}
                        hasSound={hasSound}
                        currentSessionId={currentSessionId}
                        viewportDimensions={viewportDimensions}
                        cursorRef={customCursorRef}
                        setSnackbarData={setSnackbarData}
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                        setInputFocused={setInputFocused}
                        setCursorIsHidden={setCursorIsHidden}
                    />
                )}
            />
            <Route
                path={PAGE_ROUTE.cart}
                element={(
                    <CartView
                        customCursorActive={customCursorActive}
                        currentSessionId={currentSessionId}
                        user={user}
                        userCartItems={userCartItems}
                        hasSound={hasSound}
                        setSnackbarData={setSnackbarData}
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                        setQueueRevealBuyButtons={setQueueRevealBuyButtons}
                        setUserCartItems={setUserCartItemsWithSyncGuard}
                    />
                )}
            />
            <Route
                path={`${PAGE_ROUTE.checkout}/:checkpoint`}
                element={(
                    <CheckoutView
                        customCursorActive={customCursorActive}
                        currentSessionId={currentSessionId}
                        hasSound={hasSound}
                        user={user}
                        userEmail={userEmail}
                        userCartItems={userCartItems}
                        viewportDimensions={viewportDimensions}
                        snackbarData={snackbarData}
                        setIsUserProfileDialogExpanded={onUserProfileDialogIsExpanded}
                        setInputFocused={setInputFocused}
                        setSnackbarData={setSnackbarData}
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                        setCursorIsHidden={setCursorIsHidden}
                    />
                )}
            />
            <Route
                path={PAGE_ROUTE.authAction}
                element={(
                    <AuthActionView
                        customCursorActive={customCursorActive}
                        hasSound={hasSound}
                        currentSessionId={currentSessionId}
                        setUserEmailVerified={setUserEmailVerified}
                        forceSignOutToAnonymous={forceSignOutToAnonymous}
                        setInputFocused={setInputFocused}
                        setSnackbarData={setSnackbarData}
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                    />
                )}
            />
            <Route
                path="*"
                element={(
                    <LandingView
                        customCursorActive={customCursorActive}
                        browserTabIsFocused={browserTabIsFocused}
                        hasSound={hasSound}
                        viewportDimensions={viewportDimensions}
                        landingPageContentRevealed={landingPageContentRevealed}
                        cuneiformTabletRevealed={cuneiformTabletRevealed}
                        currentSessionId={currentSessionId}
                        user={user}
                        userEmail={userEmail}
                        setSnackbarData={setSnackbarData}
                        setLandingPageContentRevealed={setLandingPageContentRevealed}
                        setCuneiformTabletRevealed={setCuneiformTabletRevealed}
                        setInputFocused={setInputFocused}
                        setCursorSigns={setCursorSigns}
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                        onCursorMouseDown={onCursorMouseDown}
                        onCursorMouseUp={onCursorMouseUp}
                    />
                )}
            />
        </Routes>
    ), [
        customCursorActive,
        browserTabIsFocused,
        user,
        userEmailVerified,
        userPostAccess,
        userEmail,
        userIsAdmin,
        fetchedUserAdminStatus,
        userCartItems,
        hasSound,
        currentSessionId,
        viewportDimensions,
        cuneiformTabletRevealed,
        landingPageContentRevealed,
        notifyUserToSignUpAfterAnnotationCreationAttempt,
        snackbarData.visible,
        stickyPostBannerActive,
        queueRevealBuyButtons,
        forceSignOutToAnonymous,
    ]);
    const shouldRenderUserProfileDialog = !!(
        user
        || (
            redirectStateChecked
            && !!auth.currentUser
            && !auth.currentUser.isAnonymous
        )
    );
    const headerButtonsUseLightSurface = !!(
        !!routeIsAuthActionPage
        || (
            stickyPostBannerActive
            && (
                !!routeIsReadPage
                || !!routeIsReadPageAnnotationFocused
            )
        )
        || !!routeIsCartPage
        || !!routeIsCheckoutPage
        || !!routeIsPrivacyPage
        || !!routeIsTermsPage
    );
    const stickyReaderBannerHeaderSurface = !!(
        stickyPostBannerActive
        && (
            !!routeIsReadPage
            || !!routeIsReadPageAnnotationFocused
        )
    );
    const showHeaderLightSurfaceBackground = !stickyReaderBannerHeaderSurface;

    return (
        <ErrorBoundary
            fallbackRender={fallbackRender}
        >
            <Container
                customCursorActive={customCursorActive}
            >
                {customCursorActive && (
                    <CustomCursor
                        ref={customCursorRef}
                        isTouchDevice={detectTouchDevice(document)}
                        // Smooth expansion but swift motion while grasped
                        isLocked={(
                            cursorType === CURSOR_TYPE.engagedSmall
                        ) && !temporarilyUnlockCursor}
                        isHidden={cursorIsHidden}
                        length={CURSOR_STANDARD_LENGTH}
                        borderWidth={CURSOR_STANDARD_BORDER_WIDTH}
                        transitionDuration={CURSOR_TRANSITION_DURATION}
                        lengthTransitionDuration={CURSOR_BORDER_LENGTH_TRANSITION_DURATION}
                        background={cursorSigns.length > 0
                            && cursorType === CURSOR_TYPE.engagedLarge
                            && !temporarilyUnlockCursor
                            ? CURSOR_EXPAND_BACKGROUND
                            : 'transparent'}
                        backgroundTranslucency={CURSOR_EXPAND_BACKGROUND_TRANSLUCENCY}
                    >
                        <SmallCursorActionContainer
                            corner={smallCursorCorner}
                            isVisible={(
                                cursorTargetType === CURSOR_TARGET.input
                                || cursorType === CURSOR_TYPE.engagedSmall
                                || cursorType === CURSOR_TYPE.line
                            ) && cursorType !== CURSOR_TYPE.engagedLarge}
                            isLineCursor={cursorType === CURSOR_TYPE.line}
                            isGrabbing={temporarilyUnlockCursor}
                            length={SMALL_CURSOR_ACTION_CONTAINER_LENGTH}
                            background={CURSOR_EXPAND_BACKGROUND}
                            backgroundTranslucency={CURSOR_EXPAND_BACKGROUND_TRANSLUCENCY}
                            transitionDuration={CURSOR_TRANSITION_DURATION}
                        >
                            {CustomCursorSigns}
                        </SmallCursorActionContainer>
                        {(cursorType === CURSOR_TYPE.engagedLarge || (
                            cursorTargetType !== CURSOR_TARGET.input
                            && cursorType !== CURSOR_TYPE.engagedSmall
                            && cursorType !== CURSOR_TYPE.line
                        )) && (
                            CustomCursorSigns
                        )}
                    </CustomCursor>
                )}
                {customCursorActive && (
                    <CursorSight
                        ref={smallCursorSightRef}
                        isVisible={
                            cursorType === CURSOR_TYPE.engagedSmall
                            && !temporarilyUnlockCursor
                        }
                        isTouchDevice={detectTouchDevice(document)}
                    />
                )}
                {RouterRoutes}
                <SoundButtonOuterContainer
                    lightBackground={headerButtonsUseLightSurface}
                    showLightBackground={showHeaderLightSurfaceBackground}
                    data-cursor-surface={headerButtonsUseLightSurface ? 'light' : undefined}
                    userProfileDialogVisible={hasUser}
                    isRecognizedUser={isRecognizedUser}
                    userLoaded={!!user}
                    userProfileDialogExpanded={userProfileDialogExpanded}
                    notificationDialogExpanded={notificationDialogExpanded}
                    transitionDuration={HEADER_BUTTON_TRANSITION_DURATION}
                >
                    <SoundButtonInnerContainer
                        lightBackground={headerButtonsUseLightSurface}
                        showLightBackground={showHeaderLightSurfaceBackground}
                        height={SOUND_BUTTON_HEIGHT}
                        customCursorActive={customCursorActive}
                        className={HOVER_TARGET_CLASSNAME}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: (e) => onCursorEnter(
                                CURSOR_TARGET.sound,
                                [CURSOR_SIGN.click],
                                e.currentTarget as HTMLElement,
                            ),
                            onMouseLeave: (e) => onCursorLeave(e),
                        })}
                        {...getPressEventHandlers({ onMouseDown: onSoundButtonClick })}
                    >
                        <SoundButton
                            dark={headerButtonsUseLightSurface}
                            viewBox={hasSound ? '0 0 12.91 12.91' : '0 0 11.37 9.7'}
                            height={SOUND_BUTTON_HEIGHT}
                            detectTouchDevice={detectTouchDevice(document)}
                        >
                            {hasSound
                                ? <Path d="M8.24,0V1.61H9.68V0ZM9.68,1.61V3.23H11.3V1.61ZM11.3,3.23V9.68h1.61V3.23Zm0,6.45H9.68V11.3H11.3V9.68ZM9.68,11.3H8.07v1.61H9.68V11.3ZM3.23,1.61V3.23H1.61V4.84H0V8.07H1.61V9.68H3.23V11.3H4.84V1.61ZM6.46,3.23V4.84H8.07V3.23ZM8.07,4.84V8.07H9.68V4.84Zm0,3.23H6.46V9.68H8.07V8.07Z" />
                                : <Path d="M3.25,0V1.62H1.62V3.23H0V6.47H1.62V8.08H3.25V9.7H4.87V0ZM6.5,3.23V4.85H8.12V3.23ZM8.12,4.85V6.47H9.75V4.85Zm1.63,0h1.62V3.23H9.75V4.85Zm0,1.62V8.08h1.62V6.47Zm-1.63,0H6.5V8.08H8.12V6.47Z" />}
                        </SoundButton>
                    </SoundButtonInnerContainer>
                </SoundButtonOuterContainer>
                <CartButton
                    dark={headerButtonsUseLightSurface}
                    showLightBackground={showHeaderLightSurfaceBackground}
                    userCartItems={userCartItems}
                    currentSessionId={currentSessionId}
                    customCursorActive={customCursorActive}
                    onCursorEnter={onCursorEnter}
                    onCursorLeave={onCursorLeave}
                />
                <Transition
                    in={shouldRenderUserProfileDialog}
                    timeout={{
                        enter: HEADER_BUTTON_TRANSITION_DURATION,
                        exit: HEADER_BUTTON_TRANSITION_DURATION,
                    }}
                    appear
                    mountOnEnter
                    unmountOnExit
                >
                    {(state) => (
                        <UserProfileDialog
                            customCursorActive={customCursorActive}
                            user={user}
                            userEmailVerified={userEmailVerified}
                            userEmail={userEmail}
                            userPostAccess={userPostAccess}
                            userCartItems={userCartItems}
                            userIsAdmin={userIsAdmin}
                            hasSound={hasSound}
                            lightBackground={headerButtonsUseLightSurface}
                            currentSessionId={currentSessionId}
                            viewportDimensions={viewportDimensions}
                            isExpanded={userProfileDialogExpanded}
                            notifyUserToSignUpAfterAnnotationCreationAttempt={notifyUserToSignUpAfterAnnotationCreationAttempt}
                            signedIn={signedIn}
                            signingInOut={signingInOut}
                            redirectStateChecked={redirectStateChecked}
                            setIsExpanded={onUserProfileDialogIsExpanded}
                            setSigningInOut={setSigningInOut}
                            setInputFocused={setInputFocused}
                            setUser={setUser}
                            setNotifyUserToSignUpAfterAnnotationCreationAttempt={setNotifyUserToSignUpAfterAnnotationCreationAttempt}
                            forceSignOutToAnonymous={forceSignOutToAnonymous}
                            onCursorEnter={onCursorEnter}
                            onCursorLeave={onCursorLeave}
                            setSnackbarData={setSnackbarData}
                            setSignedIn={setSignedIn}
                            setRefreshNotifications={setRefreshNotifications}
                            setQueueRevealBuyButtons={setQueueRevealBuyButtons}
                            style={{
                                ...FADE_IN_DEFAULT_STYLE({
                                    direction: 'left',
                                    offset: 10,
                                    duration: HEADER_BUTTON_TRANSITION_DURATION,
                                    easing: theme.motion.eagerEasing,
                                }),
                                ...FADE_IN_TRANSITION_STYLES({
                                    direction: 'left',
                                    offset: 10,
                                })[state],
                            }}
                        />
                    )}
                </Transition>
                <Transition
                    in={isRecognizedUser}
                    timeout={{
                        enter: HEADER_BUTTON_TRANSITION_DURATION,
                        exit: HEADER_BUTTON_TRANSITION_DURATION,
                    }}
                    appear
                    mountOnEnter
                    unmountOnExit
                >
                    {(state) => (
                        <NotificationDialog
                            customCursorActive={customCursorActive}
                            user={user}
                            hasSound={hasSound}
                            lightBackground={headerButtonsUseLightSurface}
                            showLightBackground={showHeaderLightSurfaceBackground}
                            currentSessionId={currentSessionId}
                            viewportDimensions={viewportDimensions}
                            isExpanded={notificationDialogExpanded}
                            isRecognizedUser={isRecognizedUser}
                            userProfileDialogExpanded={userProfileDialogExpanded}
                            refreshNotifications={refreshNotifications}
                            setIsExpanded={onNotificationDialogIsExpanded}
                            onCursorEnter={onCursorEnter}
                            onCursorLeave={onCursorLeave}
                            setRefreshNotifications={setRefreshNotifications}
                            style={{
                                ...FADE_IN_DEFAULT_STYLE({
                                    direction: 'left',
                                    offset: 10,
                                    duration: HEADER_BUTTON_TRANSITION_DURATION,
                                    easing: theme.motion.eagerEasing,
                                }),
                                ...FADE_IN_TRANSITION_STYLES({
                                    direction: 'left',
                                    offset: 10,
                                })[state],
                            }}
                        />
                    )}
                </Transition>
                {authDebugPanelEnabled && (
                    <AuthDebugPanelContainer $panelWidth={authDebugPanelWidth}>
                        <AuthDebugPanelHeader>
                            <strong>Auth Debug</strong>
                            <AuthDebugPanelButton
                                type="button"
                                onClick={() => setAuthDebugPanelExpanded((previousValue) => !previousValue)}
                            >
                                {authDebugPanelExpanded ? 'Hide' : 'Show'}
                            </AuthDebugPanelButton>
                        </AuthDebugPanelHeader>
                        <AuthDebugPanelSummary $expanded={authDebugPanelExpanded}>
                            <div>
                                Entries:
                                {' '}
                                {authDebugEntries.length}
                            </div>
                            <div>
                                Last:
                                {' '}
                                {authDebugLastEvent || 'n/a'}
                            </div>
                        </AuthDebugPanelSummary>
                        {authDebugPanelExpanded && (
                            <>
                                <AuthDebugPanelActions>
                                    <AuthDebugPanelButton
                                        type="button"
                                        onClick={refreshAuthDebugEntries}
                                    >
                                        Refresh
                                    </AuthDebugPanelButton>
                                    <AuthDebugPanelButton
                                        type="button"
                                        onClick={onCopyAuthDebugLog}
                                    >
                                        Copy JSON
                                    </AuthDebugPanelButton>
                                    <AuthDebugPanelButton
                                        type="button"
                                        onClick={onClearAuthDebugPanelLogs}
                                    >
                                        Clear
                                    </AuthDebugPanelButton>
                                    <AuthDebugPanelButton
                                        type="button"
                                        onClick={onDisableAuthDebugPanel}
                                    >
                                        Disable
                                    </AuthDebugPanelButton>
                                </AuthDebugPanelActions>
                                {authDebugCopyStatus && (
                                    <AuthDebugPanelCopyStatus>
                                        {authDebugCopyStatus}
                                    </AuthDebugPanelCopyStatus>
                                )}
                                <AuthDebugPanelTextarea
                                    readOnly
                                    value={authDebugEntriesOutput}
                                />
                            </>
                        )}
                    </AuthDebugPanelContainer>
                )}
                <Snackbar
                    data={snackbarData}
                    customCursorActive={customCursorActive}
                    hasSound={hasSound}
                    setSnackbarData={setSnackbarData}
                    onCursorEnter={onCursorEnter}
                    onCursorLeave={onCursorLeave}
                />
            </Container>
        </ErrorBoundary>
    );
}

// Workaround for lack of hot module reloading
// https://dev.to/cronokirby/react-typescript-parcel-setting-up-hot-module-reloading-4f3f
declare const module: unknown;
export default hot(module)(App);
